import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, getUser, clearAuth } from "../lib/apiClient";
import { toast } from "sonner";
import { VerifyGuards } from "../components/VerifyGuards";
import { WeeklySchedule } from "../components/WeeklySchedule";
import { SideNav } from "../components/SideNav";
import { Brand } from "../components/Brand";
import {
  Shield, LogOut, ArrowLeft, Lock, Unlock, Hash, Radio, Search,
  Ban, UserMinus, Clock, AlertTriangle, Zap, RefreshCcw,
  Volume2, ChevronRight, LayoutDashboard, Users, ScrollText, Bot, Activity,
  UsersRound, ShieldOff, RotateCcw, Flame, Crown, Sparkles, Terminal, Save, Play, Music, ShieldCheck
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import Tour from "../components/Tour";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ACTION_LABELS = {
  1: "GUILD_UPDATE", 10: "CHANNEL_CREATE", 11: "CHANNEL_UPDATE", 12: "CHANNEL_DELETE",
  20: "MEMBER_KICK", 21: "MEMBER_PRUNE", 22: "MEMBER_BAN_ADD", 23: "MEMBER_BAN_REMOVE",
  24: "MEMBER_UPDATE", 25: "MEMBER_ROLE_UPDATE", 26: "MEMBER_MOVE", 27: "MEMBER_DISCONNECT",
  30: "ROLE_CREATE", 31: "ROLE_UPDATE", 32: "ROLE_DELETE",
  72: "MESSAGE_DELETE", 73: "MESSAGE_BULK_DELETE",
};

const NAV_ITEMS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "members", label: "Members", icon: Users },
  { id: "channels", label: "Server", icon: Hash },
  { id: "cmds", label: "Commands", icon: Terminal },
  { id: "verify", label: "Verification", icon: ShieldCheck },
  { id: "audit", label: "Audit Log", icon: ScrollText },
  { id: "warns", label: "Warnings", icon: AlertTriangle },
  { id: "actions", label: "Actions", icon: Activity },
  { id: "pro", label: "ModNode+", icon: Crown },
  { id: "bot", label: "Bot Tools", icon: Bot },
];

const buildDashNav = (guildId) => {
  const by = Object.fromEntries(NAV_ITEMS.map(n => [n.id, n]));
  return [
    { label: "Overview", items: [by.overview, by.actions] },
    { label: "Moderation", items: [by.members, by.warns, by.audit, by.verify] },
    { label: "Server", items: [by.channels, by.cmds] },
    { label: "Bot & AI", items: [
      by.bot,
      { id: "ai-flags", label: "AI Moderator", icon: Sparkles, to: `/ai-flags/${guildId}`, external: true },
      { id: "protection", label: "Protection", icon: Shield, to: `/protection/${guildId}`, external: true },
      { id: "music", label: "Music", icon: Music, to: `/music/${guildId}`, external: true },
    ] },
    { label: "Premium", items: [{ ...by.pro, badge: "PRO" }] },
  ];
};

function StatCard({ label, value, accent = "cyan", testid }) {
  const bar = accent === 'cyan' ? 'bg-[#8B5CF6]' : accent === 'volt' ? 'bg-[#10B981]' : accent === 'warn' ? 'bg-[#F59E0B]' : 'bg-[#EF4444]';
  const text = accent === 'cyan' ? 'text-[#8B5CF6]' : accent === 'volt' ? 'text-[#10B981]' : accent === 'warn' ? 'text-[#F59E0B]' : 'text-[#EF4444]';
  return (
    <div data-testid={testid} className="border border-white/10 bg-[#0D0E15] p-5 relative overflow-hidden">
      <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${bar}`} />
      <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">{label}</div>
      <div className={`font-display font-black text-3xl mt-1 ${text}`}>{value}</div>
    </div>
  );
}

function StatMini({ label, value, accent = "cyan" }) {
  const text = accent === 'cyan' ? 'text-[#8B5CF6]' : accent === 'volt' ? 'text-[#10B981]' : accent === 'warn' ? 'text-[#F59E0B]' : 'text-[#EF4444]';
  return (
    <div className="border border-white/5 bg-[#06070A] p-3">
      <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">{label}</div>
      <div className={`font-display font-bold text-lg ${text} truncate`}>{value ?? "—"}</div>
    </div>
  );
}

function StatGroup({ icon: Icon, title, accent = "cyan", alert, children }) {
  const iconColor = accent === 'cyan' ? 'text-[#8B5CF6]' : accent === 'volt' ? 'text-[#10B981]' : accent === 'warn' ? 'text-[#F59E0B]' : 'text-[#EF4444]';
  return (
    <div className="border border-white/10 bg-[#0D0E15] p-5">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className={`w-4 h-4 ${iconColor}`} />}
        <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">{title}</span>
        {alert && (
          <span className="ml-auto flex items-center gap-1 px-2 py-1 bg-[#EF4444]/10 border border-[#EF4444]/50 text-[#EF4444] font-mono text-[10px] tracking-widest">
            <Flame className="w-3 h-3" /> {alert}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {children}
      </div>
    </div>
  );
}

function NavItem({ item, active, onClick, testid }) {
  const Icon = item.icon;
  return (
    <button
      data-testid={testid}
      data-tour={`nav-${item.id}`}
      onClick={onClick}
      className={`relative w-full flex items-center gap-3 px-4 py-3 font-mono text-xs tracking-widest transition-all duration-150 group ${
        active
          ? "bg-[#151225] text-white"
          : "text-[#64748B] hover:text-white hover:bg-[#0F0F18]"
      }`}
    >
      {active && (
        <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#10B981] shadow-[0_0_12px_rgba(139,92,246,0.7)]" />
      )}
      <Icon className={`w-4 h-4 ${active ? "text-[#10B981]" : ""}`} strokeWidth={2} />
      <span>{item.label}</span>
    </button>
  );
}

export default function Dashboard() {
  const { guildId } = useParams();
  const navigate = useNavigate();
  const user = getUser();

  const [section, setSection] = useState("overview");
  const dashNav = useMemo(() => buildDashNav(guildId), [guildId]);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourNonce, setTourNonce] = useState(0);  // bump to force Tour remount on replay
  const tourKey = "modnode.tour.dashboard.v1";
  const [guild, setGuild] = useState(null);
  const [channels, setChannels] = useState([]);
  const [roles, setRoles] = useState([]);
  const [audit, setAudit] = useState({ audit_log_entries: [], users: [] });
  const [actions, setActions] = useState([]);
  const [members, setMembers] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionDialog, setActionDialog] = useState(null);
  const [reason, setReason] = useState("");
  const [timeoutMins, setTimeoutMins] = useState("10");
  const [selectedChannelId, setSelectedChannelId] = useState("");
  const [slowmodeSec, setSlowmodeSec] = useState("");
  const [confirmLock, setConfirmLock] = useState(null);
  const [bans, setBans] = useState([]);
  const [banError, setBanError] = useState(null);
  const [memberError, setMemberError] = useState(null);
  const [memberTab, setMemberTab] = useState("members"); // 'members' | 'bans'
  const [initialMembersLoaded, setInitialMembersLoaded] = useState(false);
  const [stats, setStats] = useState(null);
  const [confirmRaid, setConfirmRaid] = useState(null); // 'on' | 'off'
  const [raidSlowmode, setRaidSlowmode] = useState(10);
  const [raidExempt, setRaidExempt] = useState(new Set());
  const [cmdPerms, setCmdPerms] = useState([]);
  const [verifyCfg, setVerifyCfg] = useState(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [announcementChannelId, setAnnouncementChannelId] = useState(null);
  const [announcementLoaded, setAnnouncementLoaded] = useState(false);
  const [proStatus, setProStatus] = useState(null);
  const [proSettings, setProSettings] = useState(null);
  const [proSettingsLoaded, setProSettingsLoaded] = useState(false);
  const [proSettingsBusy, setProSettingsBusy] = useState(false);
  // Scheduled Announcements (Pro)
  const [schedules, setSchedules] = useState([]);
  const [scheduleDraft, setScheduleDraft] = useState({ channel_id: "", title: "", body: "", send_at: "", recurrence: "none", image_url: "" });
  const [scheduleBusy, setScheduleBusy] = useState(false);
  // Server Snapshots (Pro)
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  // Reaction Roles (Pro)
  const [reactionRoles, setReactionRoles] = useState([]);
  const [rrDraft, setRrDraft] = useState({ channel_id: "", title: "Pick your role", body: "Tap a button to toggle a role.", exclusive: false, roles: [{ role_id: "", label: "", emoji: "", style: 2 }] });
  const [rrBusy, setRrBusy] = useState(false);
  // Security Health (Pro)
  const [secHealth, setSecHealth] = useState(null);
  const [secHealthBusy, setSecHealthBusy] = useState(false);
  // Usage Stats (Pro)
  const [usageStats, setUsageStats] = useState(null);
  const [usageDays, setUsageDays] = useState(30);
  // Weekly Report (Pro)
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [weeklyBusy, setWeeklyBusy] = useState(false);
  // Support Tickets (Pro)
  const [tickets, setTickets] = useState([]);
  const [ticketDraft, setTicketDraft] = useState({ subject: "", body: "", priority: "normal" });
  const [ticketBusy, setTicketBusy] = useState(false);
  const [cmdForms, setCmdForms] = useState({});
  const [auditCfg, setAuditCfg] = useState({ channel_id: null, enabled: false, events: [] });
  const [auditEvents, setAuditEvents] = useState([]);

  const textChannels = useMemo(
    () => channels.filter(c => [0,5,15].includes(c.type)).sort((a,b) => (a.position||0) - (b.position||0)),
    [channels]
  );

  const reloadAll = async () => {
    try {
      const [g, a, ac, w, s] = await Promise.all([
        api.get(`/guilds/${guildId}`),
        api.get(`/guilds/${guildId}/audit-logs?limit=40`).catch(() => ({ data: { audit_log_entries: [], users: [] } })),
        api.get(`/guilds/${guildId}/actions?limit=40`),
        api.get(`/guilds/${guildId}/warnings`),
        api.get(`/guilds/${guildId}/stats`).catch(() => ({ data: null })),
      ]);
      setGuild(g.data.guild);
      setChannels(g.data.channels);
      setRoles(g.data.roles);
      setAudit(a.data);
      setActions(ac.data.actions || []);
      setWarnings(w.data.warnings || []);
      setStats(s.data);
      if (!selectedChannelId && g.data.channels?.length) {
        const first = g.data.channels.find(c => c.type === 0);
        if (first) setSelectedChannelId(first.id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.discord_error?.message || "Failed to load guild");
    }
  };

  useEffect(() => {
    setLoading(true);
    reloadAll().finally(() => setLoading(false));
    // poll audit + actions every 30s (Discord rate limits are strict)
    const t = setInterval(async () => {
      try {
        const [a, ac] = await Promise.all([
          api.get(`/guilds/${guildId}/audit-logs?limit=40`),
          api.get(`/guilds/${guildId}/actions?limit=40`),
        ]);
        setAudit(a.data);
        setActions(ac.data.actions || []);
      } catch (err) { console.debug(err); }
    }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guildId]);

  useEffect(() => {
    if (!guildId) return;
    api.get(`/guilds/${guildId}/pro`).then(r => setProStatus(r.data)).catch(() => {});
  }, [guildId]);

  // First-time visitor: auto-open the guided tour (once, then persisted in localStorage).
  // We give the DOM 800ms after the guild loads so the sidebar nav items (which
  // the tour targets) are fully painted before Tour runs its querySelector.
  useEffect(() => {
    if (!guild) return;
    if (localStorage.getItem(tourKey)) return;
    const t = setTimeout(() => setTourOpen(true), 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guild]);

  // Auto-load members when Members section opens
  useEffect(() => {
    if (section === "members" && !initialMembersLoaded) {
      doSearch();
    }
    if (section === "members" && memberTab === "bans" && bans.length === 0 && !banError) {
      loadBans();
    }
    if (section === "cmds" && cmdPerms.length === 0) {
      loadCmdPerms();
    }
    if (section === "audit") {
      loadAuditCfg();
    }
    if (section === "verify" && !verifyCfg) {
      loadVerifyCfg();
    }
    if (section === "overview" && !announcementLoaded) {
      loadAnnouncementTarget();
    }
    if (section === "pro" && !proSettingsLoaded) {
      loadProSettings();
      loadSchedules();
      loadSnapshots();
      loadReactionRoles();
      loadSecurityHealth();
      loadUsageStats();
      loadWeeklyReport();
      loadTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, memberTab]);

  const loadAnnouncementTarget = async () => {
    try {
      const r = await api.get(`/guilds/${guildId}/announcement-target`);
      setAnnouncementChannelId(r.data.channel_id || null);
    } catch { /* ignore */ } finally { setAnnouncementLoaded(true); }
  };

  const loadProSettings = async () => {
    try {
      const r = await api.get(`/guilds/${guildId}/pro-settings`);
      setProSettings(r.data.settings);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Pro settings");
    } finally { setProSettingsLoaded(true); }
  };

  const saveProSettings = async (patch) => {
    setProSettingsBusy(true);
    try {
      const r = await api.put(`/guilds/${guildId}/pro-settings`, patch);
      setProSettings(r.data.settings);
      toast.success("Pro settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save Pro settings");
    } finally { setProSettingsBusy(false); }
  };

  const exportAuditCsv = async () => {
    try {
      const r = await api.get(`/guilds/${guildId}/audit-export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `modnode-audit-${guildId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Export failed — this is a ModNode+ feature.");
    }
  };

  // ---- Scheduled Announcements ----
  const loadSchedules = async () => {
    try {
      const r = await api.get(`/guilds/${guildId}/scheduled-announcements`);
      setSchedules(r.data.items || []);
    } catch { /* ignore */ }
  };
  const createSchedule = async () => {
    if (!scheduleDraft.channel_id || !scheduleDraft.title || !scheduleDraft.body || !scheduleDraft.send_at) {
      toast.error("Pick a channel, write a title/body, and set the send time.");
      return;
    }
    setScheduleBusy(true);
    try {
      const sendIso = new Date(scheduleDraft.send_at).toISOString();
      await api.post(`/guilds/${guildId}/scheduled-announcements`, { ...scheduleDraft, send_at: sendIso });
      toast.success("Announcement scheduled");
      setScheduleDraft({ channel_id: "", title: "", body: "", send_at: "", recurrence: "none", image_url: "" });
      loadSchedules();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to schedule");
    } finally { setScheduleBusy(false); }
  };
  const deleteSchedule = async (id) => {
    try {
      await api.delete(`/guilds/${guildId}/scheduled-announcements/${id}`);
      setSchedules(s => s.filter(x => x.id !== id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to cancel"); }
  };

  // ---- Server Snapshots ----
  const loadSnapshots = async () => {
    try {
      const r = await api.get(`/guilds/${guildId}/snapshots`);
      setSnapshots(r.data.items || []);
    } catch { /* ignore */ }
  };
  const takeSnapshot = async () => {
    setSnapshotBusy(true);
    try {
      const r = await api.get(`/guilds/${guildId}/snapshot`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `modnode-snapshot-${guildId}-${Date.now()}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Snapshot saved + downloaded");
      loadSnapshots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Snapshot failed — this is a ModNode+ feature.");
    } finally { setSnapshotBusy(false); }
  };
  const downloadSnapshot = async (snapId) => {
    try {
      const r = await api.get(`/guilds/${guildId}/snapshots/${snapId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `modnode-snapshot-${snapId.slice(0, 8)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
  };
  const deleteSnapshot = async (snapId) => {
    try {
      await api.delete(`/guilds/${guildId}/snapshots/${snapId}`);
      setSnapshots(s => s.filter(x => x.id !== snapId));
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // ---- Reaction Roles ----
  const loadReactionRoles = async () => {
    try {
      const r = await api.get(`/guilds/${guildId}/reaction-roles`);
      setReactionRoles(r.data.items || []);
    } catch { /* ignore */ }
  };
  const addRrEntry = () => setRrDraft(d => ({ ...d, roles: [...d.roles, { role_id: "", label: "", emoji: "", style: 2 }] }));
  const updateRrEntry = (idx, patch) => setRrDraft(d => ({ ...d, roles: d.roles.map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  const removeRrEntry = (idx) => setRrDraft(d => ({ ...d, roles: d.roles.filter((_, i) => i !== idx) }));
  const createReactionRole = async () => {
    const clean = rrDraft.roles.filter(r => r.role_id && r.label);
    if (!rrDraft.channel_id || clean.length === 0) {
      toast.error("Pick a channel and at least one role with a label.");
      return;
    }
    setRrBusy(true);
    try {
      const r = await api.post(`/guilds/${guildId}/reaction-roles`, { ...rrDraft, roles: clean });
      toast.success("Reaction-role group created");
      await api.post(`/guilds/${guildId}/reaction-roles/${r.data.item.id}/post`);
      toast.success("Posted to Discord");
      setRrDraft({ channel_id: "", title: "Pick your role", body: "Tap a button to toggle a role.", exclusive: false, roles: [{ role_id: "", label: "", emoji: "", style: 2 }] });
      loadReactionRoles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create reaction role");
    } finally { setRrBusy(false); }
  };
  const repostReactionRole = async (id) => {
    try {
      await api.post(`/guilds/${guildId}/reaction-roles/${id}/post`);
      toast.success("Reposted");
      loadReactionRoles();
    } catch (e) { toast.error(e?.response?.data?.detail || "Repost failed"); }
  };
  const deleteReactionRole = async (id) => {
    try {
      await api.delete(`/guilds/${guildId}/reaction-roles/${id}`);
      setReactionRoles(r => r.filter(x => x.id !== id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // ---- Security Health ----
  const loadSecurityHealth = async () => {
    setSecHealthBusy(true);
    try {
      const r = await api.get(`/guilds/${guildId}/security-health`);
      setSecHealth(r.data);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error(e?.response?.data?.detail || "Failed to load security report");
    } finally { setSecHealthBusy(false); }
  };

  // ---- Usage Stats ----
  const loadUsageStats = async (days = usageDays) => {
    try {
      const r = await api.get(`/guilds/${guildId}/usage-stats`, { params: { days } });
      setUsageStats(r.data);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error(e?.response?.data?.detail || "Failed to load usage stats");
    }
  };

  // ---- Weekly Report ----
  const loadWeeklyReport = async () => {
    try {
      const r = await api.get(`/guilds/${guildId}/weekly-report`);
      setWeeklyReport(r.data);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error(e?.response?.data?.detail || "Failed to load weekly report");
    }
  };
  const sendWeeklyReport = async () => {
    setWeeklyBusy(true);
    try {
      const r = await api.post(`/guilds/${guildId}/weekly-report/send`);
      toast.success(`Weekly report DM'd to <@${r.data.sent_to}>`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send weekly report");
    } finally { setWeeklyBusy(false); }
  };

  // ---- Support Tickets ----
  const loadTickets = async () => {
    try {
      const r = await api.get(`/guilds/${guildId}/support-tickets`);
      setTickets(r.data.items || []);
    } catch { /* ignore */ }
  };
  const createTicket = async () => {
    if (!ticketDraft.subject.trim() || !ticketDraft.body.trim()) {
      toast.error("Add a subject and a body.");
      return;
    }
    setTicketBusy(true);
    try {
      const r = await api.post(`/guilds/${guildId}/support-tickets`, ticketDraft);
      toast.success("Ticket submitted");
      setTickets(t => [r.data.ticket, ...t]);
      setTicketDraft({ subject: "", body: "", priority: "normal" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit ticket");
    } finally { setTicketBusy(false); }
  };

  const saveAnnouncementTarget = async (channelId) => {
    try {
      await api.put(`/guilds/${guildId}/announcement-target`, { channel_id: channelId || null });
      setAnnouncementChannelId(channelId || null);
      toast.success(channelId ? "Broadcast channel set" : "Reverted to auto-pick");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const loadVerifyCfg = async () => {
    try {
      const r = await api.get(`/guilds/${guildId}/verification`);
      setVerifyCfg(r.data.config);
    } catch { /* ignore */ }
  };

  const saveVerifyCfg = async (patch) => {
    setVerifyBusy(true);
    try {
      const r = await api.put(`/guilds/${guildId}/verification`, patch);
      setVerifyCfg(r.data.config);
      toast.success("Verification saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setVerifyBusy(false); }
  };

  const sendVerifyPrompt = async () => {
    if (!verifyCfg?.channel_id || !verifyCfg?.verified_role_id) {
      toast.error("Pick a channel and a verified role first");
      return;
    }
    setVerifyBusy(true);
    try {
      const r = await api.post(`/guilds/${guildId}/verification/send`);
      setVerifyCfg(c => ({ ...c, sent_message_id: r.data.message_id, sent_channel_id: r.data.channel_id, enabled: true }));
      toast.success("Verification prompt posted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to post prompt");
    } finally { setVerifyBusy(false); }
  };

  const loadAuditCfg = async () => {
    try {
      const r = await api.get(`/guilds/${guildId}/audit-config`);
      setAuditCfg(r.data.config);
      setAuditEvents(r.data.available_events || []);
    } catch { /* ignore */ }
  };

  const saveAuditCfg = async () => {
    try {
      await api.put(`/guilds/${guildId}/audit-config`, auditCfg);
      toast.success("Audit routing saved");
      loadAuditCfg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save audit config");
    }
  };

  const testAuditCfg = async () => {
    try {
      await api.post(`/guilds/${guildId}/audit-config/test`);
      toast.success("Test embed posted to Discord");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    }
  };

  const toggleAuditEvent = (evt) => {
    setAuditCfg(cfg => {
      const cur = new Set(cfg.events || []);
      if (cur.has(evt)) cur.delete(evt); else cur.add(evt);
      return { ...cfg, events: Array.from(cur) };
    });
  };

  const loadCmdPerms = async () => {
    try {
      const r = await api.get(`/guilds/${guildId}/command-perms`);
      setCmdPerms(r.data.perms || []);
    } catch (e) {
      toast.error("Failed to load command permissions");
    }
  };

  const saveCmdPerm = async (cmdKey, roleIds) => {
    try {
      await api.put(`/guilds/${guildId}/command-perms/${cmdKey}`, { allowed_role_ids: roleIds });
      toast.success(`${cmdKey.toUpperCase()} → ${roleIds.length ? roleIds.length + ' role(s)' : 'admin-only'}`);
      loadCmdPerms();
    } catch {
      toast.error(`Failed to save perms for ${cmdKey}`);
    }
  };

  const setForm = (cmdKey, field, value) => {
    setCmdForms(prev => ({ ...prev, [cmdKey]: { ...(prev[cmdKey] || {}), [field]: value } }));
  };

  const runCmd = async (cmdKey) => {
    const f = cmdForms[cmdKey] || {};
    try {
      const uid = f.user_id?.trim();
      const reason = f.reason?.trim() || `via /cmds panel`;
      if (cmdKey === "warn") {
        if (!uid) return toast.error("user_id required");
        await api.post(`/guilds/${guildId}/warnings`, { user_id: uid, username: f.username, reason });
      } else if (cmdKey === "timeout") {
        if (!uid) return toast.error("user_id required");
        await api.post(`/guilds/${guildId}/members/${uid}/timeout`, { minutes: parseInt(f.minutes || "10"), reason });
      } else if (cmdKey === "untimeout") {
        if (!uid) return toast.error("user_id required");
        await api.post(`/guilds/${guildId}/members/${uid}/timeout`, { minutes: 0, reason: reason || "cleared" });
      } else if (cmdKey === "kick") {
        if (!uid) return toast.error("user_id required");
        await api.post(`/guilds/${guildId}/members/${uid}/kick`, { reason });
      } else if (cmdKey === "ban") {
        if (!uid) return toast.error("user_id required");
        await api.post(`/guilds/${guildId}/members/${uid}/ban`, { reason, delete_message_seconds: parseInt(f.purge_seconds || "0") });
      } else if (cmdKey === "unban") {
        if (!uid) return toast.error("user_id required");
        await api.delete(`/guilds/${guildId}/bans/${uid}`);
      } else if (cmdKey === "purge") {
        if (!f.channel_id) return toast.error("channel required");
        const msgs = await api.get(`/channels/${f.channel_id}/messages?limit=${parseInt(f.count || "10")}`);
        const ids = msgs.data.messages.map(m => m.id);
        if (!ids.length) return toast.info("no messages");
        await api.post(`/channels/${f.channel_id}/purge`, { message_ids: ids, reason });
      } else if (cmdKey === "slowmode") {
        if (!f.channel_id) return toast.error("channel required");
        await api.post(`/channels/${f.channel_id}/slowmode`, { rate_limit_per_user: parseInt(f.seconds || "0"), reason });
      } else if (cmdKey === "lockdown") {
        await api.post(`/guilds/${guildId}/lockdown`, { reason });
      } else if (cmdKey === "unlock") {
        await api.post(`/guilds/${guildId}/unlock`, { reason });
      } else if (cmdKey === "raidmode") {
        await api.post(`/guilds/${guildId}/raidmode`, { enabled: true, slowmode_seconds: parseInt(f.slowmode || "10") });
      } else if (cmdKey === "nickname") {
        if (!uid) return toast.error("user_id required");
        await api.patch(`/guilds/${guildId}/members/${uid}/nickname`, { nick: f.nick || "", reason });
      } else if (cmdKey === "role_add") {
        if (!uid || !f.role_id) return toast.error("user_id and role required");
        await api.post(`/guilds/${guildId}/members/${uid}/roles`, { role_id: f.role_id, reason });
      } else if (cmdKey === "role_remove") {
        if (!uid || !f.role_id) return toast.error("user_id and role required");
        await api.delete(`/guilds/${guildId}/members/${uid}/roles/${f.role_id}`);
      }
      toast.success(`${cmdKey.toUpperCase()} executed`);
      reloadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.discord_error?.message || e?.response?.data?.detail || `${cmdKey} failed`);
    }
  };

  const doSearch = async () => {
    setMemberError(null);
    try {
      const q = search.trim();
      const url = q
        ? `/guilds/${guildId}/members?search=${encodeURIComponent(q)}&limit=50`
        : `/guilds/${guildId}/members?limit=50`;
      const r = await api.get(url);
      setMembers(r.data.members || []);
      setInitialMembersLoaded(true);
    } catch (e) {
      setMemberError(e?.response?.data?.detail?.discord_error?.message || "Failed to load members");
      setMembers([]);
    }
  };

  const loadBans = async () => {
    setBanError(null);
    try {
      const r = await api.get(`/guilds/${guildId}/bans?limit=100`);
      setBans(r.data.bans || []);
    } catch (e) {
      setBanError(e?.response?.data?.detail?.discord_error?.message || "Failed to load bans");
      setBans([]);
    }
  };

  const runAction = async () => {
    const { type, member } = actionDialog || {};
    if (!type || !member) return;
    const uid = member.user.id;
    try {
      if (type === "kick") await api.post(`/guilds/${guildId}/members/${uid}/kick`, { reason });
      if (type === "ban") await api.post(`/guilds/${guildId}/members/${uid}/ban`, { reason, delete_message_seconds: 0 });
      if (type === "timeout") await api.post(`/guilds/${guildId}/members/${uid}/timeout`, { minutes: parseInt(timeoutMins || "10"), reason });
      if (type === "warn") await api.post(`/guilds/${guildId}/warnings`, { user_id: uid, username: member.user.username, reason });
      toast.success(`${type.toUpperCase()} applied to ${member.user.username}`);
      setActionDialog(null); setReason(""); setTimeoutMins("10");
      reloadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.discord_error?.message || `${type} failed`);
    }
  };

  const doLockdown = async (mode) => {
    try {
      const url = mode === "lock" ? `/guilds/${guildId}/lockdown` : `/guilds/${guildId}/unlock`;
      const r = await api.post(url, { reason: `ModNode ${mode.toUpperCase()} via console` });
      toast.success(`${mode==='lock'?'LOCKED':'UNLOCKED'} ${r.data.channels_locked || r.data.channels_unlocked} channels`);
      setConfirmLock(null);
      reloadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.discord_error?.message || "Lockdown failed");
    }
  };

  const doRaidMode = async (mode) => {
    try {
      const payload = mode === "on"
        ? { enabled: true, slowmode_seconds: raidSlowmode, exempt_channel_ids: Array.from(raidExempt) }
        : { enabled: false, slowmode_seconds: 0, exempt_channel_ids: [] };
      const r = await api.post(`/guilds/${guildId}/raidmode`, payload);
      toast.success(`RAID MODE ${r.data.enabled ? "ENGAGED" : "STOOD DOWN"} · ${r.data.channels_touched} channels${r.data.channels_exempted ? ` · ${r.data.channels_exempted} exempted` : ""}`);
      setConfirmRaid(null);
      setRaidExempt(new Set());
      reloadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.discord_error?.message || "Raid mode failed");
    }
  };

  const toggleExempt = (chId) => {
    setRaidExempt(prev => {
      const next = new Set(prev);
      if (next.has(chId)) next.delete(chId); else next.add(chId);
      return next;
    });
  };

  const applySlowmode = async () => {
    if (!selectedChannelId) { toast.error("Pick a channel"); return; }
    try {
      await api.post(`/channels/${selectedChannelId}/slowmode`, { rate_limit_per_user: parseInt(slowmodeSec || "0") });
      toast.success("Slowmode applied");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.discord_error?.message || "Failed");
    }
  };

  const purgeChannel = async (count) => {
    if (!selectedChannelId) { toast.error("Pick a channel"); return; }
    try {
      const msgs = await api.get(`/channels/${selectedChannelId}/messages?limit=${count}`);
      const ids = msgs.data.messages.map(m => m.id);
      if (ids.length === 0) { toast.info("No messages"); return; }
      const r = await api.post(`/channels/${selectedChannelId}/purge`, { message_ids: ids, reason: `Purge ${count} via ModNode` });
      toast.success(`Purged ${r.data.deleted} messages`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.discord_error?.message || "Purge failed");
    }
  };

  const logout = () => { clearAuth(); navigate("/"); };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#06070A] flex items-center justify-center">
        <div className="font-mono text-[#10B981] tracking-widest text-sm">
          <span className="pulse-dot inline-block w-2 h-2 bg-[#10B981] mr-3" />
          LOADING GUILD DATA...
        </div>
      </div>
    );
  }

  if (!guild) {
    return (
      <div className="min-h-screen bg-[#06070A] flex flex-col items-center justify-center gap-4 p-8">
        <AlertTriangle className="w-12 h-12 text-[#EF4444]" />
        <div className="font-display text-xl">Could not load guild</div>
        <Button onClick={() => navigate("/servers")} data-testid="back-to-servers-btn">Back to servers</Button>
      </div>
    );
  }

  // Kick off the guided tour the first time someone lands on the dashboard.
  const TOUR_STEPS = [
    { selector: '[data-tour="topbar-brand"]',      title: "This is your Command Bar", body: "Every ModNode action lives inside this dashboard. The green shield up top is always your quick-eject back to Servers." },
    { selector: '[data-tour="nav-overview"]',      title: "Overview",       body: "Live server pulse — member count, moderation totals, and shortcuts to the tools you'll use most." },
    { selector: '[data-tour="nav-members"]',       title: "Members",        body: "Full member list with search. Warn, kick, timeout, or ban anyone in one click." },
    { selector: '[data-tour="nav-channels"]',      title: "Server",         body: "Channel-level controls: lockdown, purge, raid mode, permission changes — all logged." },
    { selector: '[data-tour="nav-cmds"]',          title: "Command Permissions", body: "Assign which staff roles can run which mod commands. Fine-grained permissions per action." },
    { selector: '[data-tour="nav-verify"]',        title: "Verification",   body: "Post a Discord OAuth verify button in any channel. Members click, log in with Discord, and receive a role." },
    { selector: '[data-tour="nav-audit"]',         title: "Audit Log",      body: "Discord's native audit log mirrored here with filters, colored tags, and V2 embed announcements." },
    { selector: '[data-tour="nav-warns"]',         title: "Warnings",       body: "Every warning ever issued in the server — searchable, revocable, exportable." },
    { selector: '[data-tour="nav-actions"]',       title: "Actions",        body: "ModNode's own action log: every bot-driven kick, ban, timeout, delete, and music command." },
    { selector: '[data-tour="nav-bot"]',           title: "Bot Tools",      body: "AI Moderator, Music Bot, Protection, and the global Founder Console all live one hop away." },
  ];

  return (
    <div className="min-h-screen bg-[#06070A] text-white" data-testid="dashboard-page">
      <div className="relative z-10 flex flex-col lg:flex-row">
      <SideNav
        groups={dashNav}
        header={<Brand size="sm" onClick={() => navigate("/servers")} />}
        active={section}
        onSelect={item => item.to ? navigate(item.to) : setSection(item.id)}
        footer={
          <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-2">Roles · {roles.length}</div>
            <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
              {roles.filter(r => r.name !== '@everyone').sort((a,b) => b.position - a.position).slice(0, 30).map(r => (
                <div key={r.id} className="flex items-center gap-2 text-xs" data-testid={`role-${r.id}`}>
                  <span className="w-2 h-2 rounded-full shrink-0" style={{background: r.color ? `#${r.color.toString(16).padStart(6,'0')}` : '#64748B'}} />
                  <span className="truncate text-[#94A3B8]">{r.name}</span>
                </div>
              ))}
            </div>
          </div>
        }
      />
      <div className="flex-1 min-w-0">
      {/* Top nav */}
      <div className="relative lg:sticky lg:top-0 z-20 border-b border-white/5 bg-[#06070A]/70 backdrop-blur-xl flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-4">
          <button data-testid="back-btn" onClick={() => navigate("/servers")} className="p-2 border border-white/10 hover:border-[#10B981] hover:text-[#10B981] transition-all">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3" data-tour="topbar-brand">
            {guild.icon_url ? (
              <img src={guild.icon_url} alt="" className="w-10 h-10 rounded-md" />
            ) : (
              <div className="w-10 h-10 rounded-md bg-[#141622] flex items-center justify-center font-display font-bold text-[#8B5CF6]">
                {guild.name?.slice(0,2).toUpperCase()}
              </div>
            )}
            <div>
              <div className="font-display font-bold text-lg leading-tight flex items-center gap-2">
                {guild.name}
                {proStatus?.is_pro && (
                  <span data-testid="pro-badge" title={proStatus?.grant?.note || "This server has ModNode+"} className="px-1.5 py-0.5 border border-[#F59E0B]/50 bg-[#F59E0B]/10 text-[#F59E0B] font-mono text-[9px] tracking-widest">◉ MODNODE+</span>
                )}
              </div>
              <div className="font-mono text-[10px] text-[#64748B] tracking-widest">{guild.id}</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 ml-6 px-3 py-1.5 border border-[#10B981]/30">
            <span className="w-1.5 h-1.5 bg-[#10B981] pulse-dot" />
            <span className="font-mono text-[10px] tracking-widest text-[#10B981]">CONSOLE LIVE</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={reloadAll} data-testid="refresh-btn" className="p-2 border border-white/10 hover:border-[#10B981] hover:text-[#10B981] transition-all">
            <RefreshCcw className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              localStorage.removeItem(tourKey);
              setTourOpen(false);
              setTourNonce(n => n + 1);
              // small delay so the previous tour teardown completes before we
              // remount with a fresh key/state — otherwise React batches the
              // updates and nothing changes on screen
              setTimeout(() => setTourOpen(true), 30);
              toast.success("Replaying tour…");
            }}
            data-testid="restart-tour-btn"
            title="Replay the guided tour"
            className="p-2 border border-white/10 hover:border-[#8B5CF6] hover:text-[#8B5CF6] transition-all"
          >
            <Sparkles className="w-4 h-4" />
          </button>
          <span className="hidden md:block text-sm text-[#94A3B8]">{user?.global_name || user?.username}</span>
          <button data-testid="dashboard-logout-btn" onClick={logout} className="p-2 border border-white/10 hover:border-[#EF4444] hover:text-[#EF4444] transition-all">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-0">
        {/* MIDDLE: dynamic section */}
        <main className="p-6 lg:p-8 min-w-0 space-y-5">
          {/* Section header */}
          <div className="flex items-end justify-between pb-2">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#A78BFA]">{guild.name}</div>
              <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight" data-testid="section-title">
                {NAV_ITEMS.find(n => n.id === section)?.label}
              </h2>
            </div>
            <div className="font-mono text-[11px] text-[#64748B]">
              {new Date().toLocaleTimeString([], { hour12: false })}
            </div>
          </div>

          {section === "overview" && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard testid="stat-members" label="Members" value={guild.approximate_member_count?.toLocaleString() || "—"} accent="cyan" />
                <StatCard testid="stat-online" label="Online" value={guild.approximate_presence_count?.toLocaleString() || "—"} accent="volt" />
                <StatCard testid="stat-channels" label="Channels" value={channels.length} accent="warn" />
                <StatCard testid="stat-roles" label="Roles" value={roles.length} accent="danger" />
              </div>

              {/* Broadcast Channel — server owner picks where global announcements land here */}
              <div className="border border-[#8B5CF6]/25 bg-[#0D0E15] p-5" data-testid="broadcast-channel-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Radio className="w-4 h-4 text-[#8B5CF6]" />
                    <span className="font-mono text-[10px] tracking-widest text-[#8B5CF6] uppercase">Broadcast Channel</span>
                  </div>
                  <span className="font-mono text-[10px] text-[#64748B]">
                    {announcementChannelId
                      ? <>◉ posts to #{(channels.find(c => c.id === announcementChannelId) || {}).name || announcementChannelId}</>
                      : <>○ auto-pick (system channel)</>
                    }
                  </span>
                </div>
                <p className="text-xs text-[#94A3B8] mb-3">
                  Pick where <span className="text-[#8B5CF6]">global ModNode announcements</span> land in this server. Leave on auto to use the system channel (or the first text channel the bot can see).
                </p>
                <select
                  data-testid="broadcast-channel-select"
                  value={announcementChannelId || ""}
                  onChange={e => saveAnnouncementTarget(e.target.value || null)}
                  className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#8B5CF6] font-mono"
                >
                  <option value="">— auto-pick (system channel) —</option>
                  {channels.filter(c => c.type === 0).map(c => (
                    <option key={c.id} value={c.id}>#{c.name}</option>
                  ))}
                </select>
                <div className="mt-1 font-mono text-[10px] text-[#64748B]">
                  // saves instantly · overrides the founder-level picker for this server
                </div>
              </div>

              {stats && (
                <div className="space-y-4" data-testid="rich-stats">
                  {/* Identity */}
                  {(stats.banner_url || stats.icon_url) && (
                    <div className="relative border border-white/10 bg-[#0D0E15] overflow-hidden">
                      {stats.banner_url && (
                        <div className="h-32 md:h-40 relative">
                          <img src={stats.banner_url} alt="" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0E15] via-[#0D0E15]/40 to-transparent" />
                        </div>
                      )}
                      <div className="p-5 flex items-center gap-4 -mt-8 relative">
                        {stats.icon_url && <img src={stats.icon_url} alt="" className="w-20 h-20 rounded-md border-2 border-[#0D0E15]" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-black text-xl truncate">{stats.name}</div>
                          {stats.description && <div className="text-sm text-[#94A3B8] truncate">{stats.description}</div>}
                          <div className="flex flex-wrap gap-3 mt-2 font-mono text-[10px] text-[#64748B] tracking-widest">
                            <span>ID: {stats.id}</span>
                            <span>· LOCALE: {stats.preferred_locale}</span>
                            {stats.vanity_url_code && <span>· discord.gg/{stats.vanity_url_code}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* People */}
                  <StatGroup icon={Users} title="People" accent="cyan">
                    <StatMini label="Members" value={stats.members?.toLocaleString()} accent="cyan" />
                    <StatMini label="Online" value={stats.online?.toLocaleString()} accent="volt" />
                    <StatMini label="Max Members" value={stats.max_members?.toLocaleString()} accent="cyan" />
                    <StatMini label="Total Bans" value={stats.bans} accent="danger" />
                    <StatMini label="Invites" value={stats.invites} accent="warn" />
                    <StatMini label="Max Video" value={stats.max_video_channel_users} accent="cyan" />
                  </StatGroup>

                  {/* Security */}
                  <StatGroup icon={Shield} title="Security" accent="danger" alert={stats.raid_mode_active ? "RAID MODE ACTIVE" : null}>
                    <StatMini label="Verification" value={stats.verification_level} accent={stats.verification_level_raw >= 3 ? "danger" : "cyan"} />
                    <StatMini label="Explicit Filter" value={stats.explicit_content_filter} accent="warn" />
                    <StatMini label="Notifications" value={stats.default_notifications} accent="cyan" />
                    <StatMini label="2FA Required" value={stats.mfa_level} accent={stats.mfa_level === "ELEVATED" ? "volt" : "warn"} />
                    <StatMini label="NSFW Level" value={stats.nsfw_level} accent="warn" />
                    <StatMini label="Widget" value={stats.widget_enabled ? "ENABLED" : "OFF"} accent="cyan" />
                  </StatGroup>

                  {/* Channels */}
                  <StatGroup icon={Hash} title="Channels" accent="warn">
                    <StatMini label="Total" value={stats.channels_total} accent="warn" />
                    <StatMini label="Text" value={stats.text_channels} accent="cyan" />
                    <StatMini label="Voice" value={stats.voice_channels} accent="volt" />
                    <StatMini label="Announcement" value={stats.announcement_channels} accent="cyan" />
                    <StatMini label="Forum" value={stats.forum_channels} accent="cyan" />
                    <StatMini label="Stage" value={stats.stage_channels} accent="volt" />
                    <StatMini label="Categories" value={stats.categories} accent="warn" />
                    <StatMini label="Slowmode" value={stats.slowmode_channels} accent="warn" />
                    <StatMini label="NSFW" value={stats.nsfw_channels} accent="danger" />
                  </StatGroup>

                  {/* Roles */}
                  <StatGroup icon={Crown} title="Roles" accent="danger">
                    <StatMini label="Total" value={stats.roles} accent="danger" />
                    <StatMini label="Hoisted" value={stats.hoisted_roles} accent="warn" />
                    <StatMini label="Bot-Managed" value={stats.managed_roles} accent="cyan" />
                    <StatMini label="Mentionable" value={stats.mentionable_roles} accent="volt" />
                  </StatGroup>

                  {/* Boosts */}
                  <StatGroup icon={Sparkles} title="Boosts" accent="volt">
                    <StatMini label="Tier" value={stats.boost_tier} accent={stats.boost_tier_raw >= 2 ? "volt" : "cyan"} />
                    <StatMini label="Active Boosts" value={stats.boost_count} accent="volt" />
                    <StatMini label="Progress Bar" value={stats.premium_progress_bar_enabled ? "ON" : "OFF"} accent="volt" />
                  </StatGroup>

                  {/* Content */}
                  <StatGroup icon={Zap} title="Content" accent="cyan">
                    <StatMini label="Emojis" value={stats.emojis_total} accent="cyan" />
                    <StatMini label="Animated" value={stats.emojis_animated} accent="volt" />
                    <StatMini label="Static" value={stats.emojis_static} accent="cyan" />
                    <StatMini label="Stickers" value={stats.stickers} accent="warn" />
                  </StatGroup>

                  {/* Moderation stats */}
                  <StatGroup icon={AlertTriangle} title="Moderation" accent="warn">
                    <StatMini label="Warnings Issued" value={stats.warnings_issued} accent="warn" />
                    <StatMini label="Mod Actions" value={stats.mod_actions_total} accent="danger" />
                    <StatMini label="AFK Timeout" value={stats.afk_timeout ? `${stats.afk_timeout / 60}m` : "—"} accent="cyan" />
                    <StatMini label="Created" value={stats.created_at ? new Date(stats.created_at).toLocaleDateString() : "—"} accent="volt" />
                  </StatGroup>

                  {/* Features */}
                  {stats.features?.length > 0 && (
                    <div className="border border-white/10 bg-[#0D0E15] p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-4 h-4 text-[#8B5CF6]" />
                        <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Guild Features · {stats.features.length}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {stats.features.map(f => (
                          <span key={f} className="px-2 py-0.5 border border-[#8B5CF6]/20 bg-[#8B5CF6]/5 font-mono text-[9px] text-[#8B5CF6] tracking-widest">{f}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-white/10 bg-[#0D0E15] p-5">
                  <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-4">Recent Discord Audit</div>
                  <div className="space-y-1 font-mono text-xs max-h-[280px] overflow-y-auto">
                    {(audit?.audit_log_entries || []).slice(0, 8).map((e, i) => {
                      const label = ACTION_LABELS[e.action_type] || `ACTION_${e.action_type}`;
                      const color = [20,22,72,73].includes(e.action_type) ? "text-[#EF4444]" : "text-[#8B5CF6]";
                      const usr = audit.users?.find(u => u.id === e.user_id);
                      return (
                        <div key={e.id || i} className="flex gap-3 py-1 border-b border-white/5">
                          <span className={color}>{label}</span>
                          <span className="text-[#94A3B8] truncate">@{usr?.username || "system"}</span>
                        </div>
                      );
                    })}
                    {(audit?.audit_log_entries || []).length === 0 && <div className="text-[#64748B]">// no recent activity</div>}
                  </div>
                </div>

                <div className="border border-white/10 bg-[#0D0E15] p-5">
                  <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-4">Recent ModNode Actions</div>
                  <div className="space-y-1 font-mono text-xs max-h-[280px] overflow-y-auto">
                    {actions.slice(0, 8).map(a => (
                      <div key={a.id} className="flex gap-3 py-1 border-b border-white/5">
                        <span className="text-[#64748B]">{new Date(a.created_at).toLocaleTimeString([], {hour12: false})}</span>
                        <span className={`${a.action==='ban'||a.action==='lockdown'||a.action==='raid_mode'?'text-[#EF4444]': a.action==='timeout'||a.action==='kick'?'text-[#F59E0B]':'text-[#10B981]'} uppercase`}>{a.action}</span>
                      </div>
                    ))}
                    {actions.length === 0 && <div className="text-[#64748B]">// nothing yet</div>}
                  </div>
                </div>
              </div>
            </>
          )}

          {section === "members" && (
            <div className="space-y-4">
              {/* Tabs: MEMBERS / BANS */}
              <div className="flex items-center gap-8 border-b border-white/10">
                <button
                  data-testid="tab-members"
                  onClick={() => setMemberTab("members")}
                  className={`relative flex items-center gap-2 py-3 font-mono text-xs tracking-widest transition-colors ${
                    memberTab === "members" ? "text-white" : "text-[#64748B] hover:text-white"
                  }`}
                >
                  <UsersRound className={`w-4 h-4 ${memberTab === "members" ? "text-[#10B981]" : ""}`} />
                  MEMBERS ({members.length})
                  {memberTab === "members" && (
                    <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#10B981] shadow-[0_0_8px_rgba(139,92,246,0.7)]" />
                  )}
                </button>
                <button
                  data-testid="tab-bans"
                  onClick={() => setMemberTab("bans")}
                  className={`relative flex items-center gap-2 py-3 font-mono text-xs tracking-widest transition-colors ${
                    memberTab === "bans" ? "text-white" : "text-[#64748B] hover:text-white"
                  }`}
                >
                  <ShieldOff className={`w-4 h-4 ${memberTab === "bans" ? "text-[#10B981]" : ""}`} />
                  BANS ({bans.length})
                  {memberTab === "bans" && (
                    <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#10B981] shadow-[0_0_8px_rgba(139,92,246,0.7)]" />
                  )}
                </button>
                <button
                  onClick={() => { if (memberTab === "members") doSearch(); else loadBans(); }}
                  data-testid="tab-reload-btn"
                  className="ml-auto flex items-center gap-1.5 py-3 font-mono text-[10px] tracking-widest text-[#64748B] hover:text-[#10B981] transition-colors"
                >
                  <RotateCcw className="w-3 h-3" /> RELOAD
                </button>
              </div>

              {/* Error banner */}
              {memberTab === "members" && memberError && (
                <div data-testid="members-error" className="border border-[#EF4444]/50 bg-[#EF4444]/10 px-4 py-2.5 font-mono text-xs text-[#EF4444]">
                  {memberError}
                </div>
              )}
              {memberTab === "bans" && banError && (
                <div data-testid="bans-error" className="border border-[#EF4444]/50 bg-[#EF4444]/10 px-4 py-2.5 font-mono text-xs text-[#EF4444]">
                  {banError}
                </div>
              )}

              {/* Search input (only for members tab) */}
              {memberTab === "members" && (
                <div className="relative border border-white/10 bg-[#08090D] focus-within:border-[#10B981]/60 transition-colors">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <input
                    data-testid="member-search-input"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && doSearch()}
                    placeholder="Search by username, nickname, or ID..."
                    className="w-full bg-transparent pl-11 pr-24 py-3.5 text-sm outline-none placeholder:text-[#64748B]"
                  />
                  <button
                    data-testid="member-search-btn"
                    onClick={doSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-[#10B981] text-black font-mono text-[10px] tracking-widest font-bold hover:bg-[#10B981]/80"
                  >SEARCH</button>
                </div>
              )}

              {/* Members list */}
              {memberTab === "members" && (
                <div className="space-y-2 max-h-[65vh] overflow-y-auto" data-testid="members-results">
                  {members.length === 0 && !memberError && (
                    <div className="text-center py-16 font-mono text-xs text-[#64748B] border border-dashed border-white/5">
                      // no members loaded
                    </div>
                  )}
                  {members.map(m => (
                    <div key={m.user.id} className="flex items-center gap-3 p-3 border border-white/5 hover:border-[#10B981]/40 bg-[#08090D] transition-colors">
                      {m.user.avatar ? (
                        <img src={`https://cdn.discordapp.com/avatars/${m.user.id}/${m.user.avatar}.png?size=64`} alt="" className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#141622] flex items-center justify-center font-mono text-xs text-[#94A3B8]">
                          {m.user.username?.slice(0,2).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{m.nick || m.user.global_name || m.user.username}</div>
                        <div className="font-mono text-[10px] text-[#64748B]">@{m.user.username} · {m.user.id}</div>
                        {m.communication_disabled_until && new Date(m.communication_disabled_until) > new Date() && (
                          <div className="mt-1 inline-block px-1.5 py-0.5 bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#F59E0B] font-mono text-[9px] tracking-widest">TIMED OUT</div>
                        )}
                      </div>
                      <div className="flex gap-1 flex-wrap justify-end">
                        <ActionBtn testid={`warn-${m.user.id}`} icon={AlertTriangle} label="WARN" color="warn" onClick={() => setActionDialog({ type: "warn", member: m })} />
                        <ActionBtn testid={`timeout-${m.user.id}`} icon={Clock} label="TIMEOUT" color="warn" onClick={() => setActionDialog({ type: "timeout", member: m })} />
                        <ActionBtn testid={`kick-${m.user.id}`} icon={UserMinus} label="KICK" color="danger" onClick={() => setActionDialog({ type: "kick", member: m })} />
                        <ActionBtn testid={`ban-${m.user.id}`} icon={Ban} label="BAN" color="danger" onClick={() => setActionDialog({ type: "ban", member: m })} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Bans list */}
              {memberTab === "bans" && (
                <div className="space-y-2 max-h-[65vh] overflow-y-auto" data-testid="bans-results">
                  {bans.length === 0 && !banError && (
                    <div className="text-center py-16 font-mono text-xs text-[#64748B] border border-dashed border-white/5">
                      // no bans on record
                    </div>
                  )}
                  {bans.map(b => (
                    <div key={b.user.id} className="flex items-center gap-3 p-3 border border-white/5 hover:border-[#EF4444]/40 bg-[#08090D] transition-colors">
                      {b.user.avatar ? (
                        <img src={`https://cdn.discordapp.com/avatars/${b.user.id}/${b.user.avatar}.png?size=64`} alt="" className="w-10 h-10 rounded-full grayscale" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#141622] flex items-center justify-center font-mono text-xs text-[#EF4444]">
                          {b.user.username?.slice(0,2).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{b.user.global_name || b.user.username}</div>
                        <div className="font-mono text-[10px] text-[#64748B]">@{b.user.username} · {b.user.id}</div>
                        {b.reason && <div className="text-xs text-[#94A3B8] mt-1">Reason: {b.reason}</div>}
                      </div>
                      <button
                        data-testid={`unban-${b.user.id}`}
                        onClick={async () => {
                          try {
                            await api.delete(`/guilds/${guildId}/bans/${b.user.id}`);
                            toast.success(`Unbanned ${b.user.username}`);
                            loadBans();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail?.discord_error?.message || "Unban failed");
                          }
                        }}
                        className="px-3 py-1.5 border border-[#10B981]/40 hover:border-[#10B981] hover:text-[#10B981] font-mono text-[10px] tracking-widest transition-all"
                      >UNBAN</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {section === "channels" && (
            <>
              <div className="border border-white/10 bg-[#0D0E15] p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Volume2 className="w-4 h-4 text-[#10B981]" />
                  <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Channel Ops</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Target Channel</label>
                    <Select value={selectedChannelId} onValueChange={setSelectedChannelId}>
                      <SelectTrigger data-testid="channel-select" className="bg-[#06070A] border-white/10 rounded-none">
                        <SelectValue placeholder="Pick channel" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0D0E15] border-white/10 rounded-none">
                        {textChannels.map(c => (
                          <SelectItem key={c.id} value={c.id}>#{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Slowmode (sec)</label>
                    <div className="flex gap-2">
                      <Input data-testid="slowmode-input" value={slowmodeSec} onChange={e => setSlowmodeSec(e.target.value)} type="number" placeholder="0-21600" className="bg-[#06070A] border-white/10 rounded-none" />
                      <Button data-testid="slowmode-apply-btn" onClick={applySlowmode} className="rounded-none bg-[#F59E0B] text-black hover:bg-[#F59E0B]/80 font-bold">SET</Button>
                    </div>
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Purge Messages</label>
                    <div className="flex gap-2">
                      <Button data-testid="purge-10-btn" onClick={() => purgeChannel(10)} variant="outline" className="rounded-none border-white/10 hover:border-[#EF4444] hover:text-[#EF4444]">10</Button>
                      <Button data-testid="purge-50-btn" onClick={() => purgeChannel(50)} variant="outline" className="rounded-none border-white/10 hover:border-[#EF4444] hover:text-[#EF4444]">50</Button>
                      <Button data-testid="purge-100-btn" onClick={() => purgeChannel(100)} variant="outline" className="rounded-none border-white/10 hover:border-[#EF4444] hover:text-[#EF4444]">100</Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-white/10 bg-[#0D0E15]">
                <div className="px-5 py-3 border-b border-white/10 font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Text Channels · {textChannels.length}</div>
                <div className="max-h-[500px] overflow-y-auto" data-testid="channels-list">
                  {textChannels.map(c => (
                    <button
                      key={c.id}
                      data-testid={`channel-${c.id}`}
                      onClick={() => setSelectedChannelId(c.id)}
                      className={`w-full text-left px-5 py-2.5 border-b border-white/5 flex items-center gap-2 text-sm hover:bg-[#141622] transition-all ${selectedChannelId === c.id ? 'bg-[#141622] border-l-2 border-l-[#10B981]' : ''}`}
                    >
                      <Hash className="w-3.5 h-3.5 text-[#64748B]" />
                      <span className="truncate">{c.name}</span>
                      {c.rate_limit_per_user > 0 && (
                        <span className="ml-auto font-mono text-[10px] text-[#F59E0B] flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {c.rate_limit_per_user}s
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {section === "audit" && (
            <div className="space-y-4">
              {/* Audit routing config */}
              <div className="border border-white/10 bg-[#0D0E15] p-5" data-testid="audit-config">
                <div className="flex items-center gap-2 mb-4">
                  <ScrollText className="w-4 h-4 text-[#10B981]" />
                  <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Audit Routing</span>
                  <span className={`ml-auto flex items-center gap-1 px-2 py-1 font-mono text-[10px] tracking-widest ${auditCfg.enabled && auditCfg.channel_id ? "bg-[#10B981]/10 border border-[#10B981]/40 text-[#10B981]" : "bg-white/5 border border-white/10 text-[#64748B]"}`}>
                    <span className={`w-1.5 h-1.5 ${auditCfg.enabled && auditCfg.channel_id ? "bg-[#10B981] pulse-dot" : "bg-[#64748B]"}`} />
                    {auditCfg.enabled && auditCfg.channel_id ? "ROUTING LIVE" : "DISABLED"}
                  </span>
                </div>
                <p className="text-xs text-[#94A3B8] mb-4">
                  Route every ModNode action to a Discord channel as a rich embed. Useful for mod-log channels visible only to staff.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Target Channel</label>
                    <Select
                      value={auditCfg.channel_id || ""}
                      onValueChange={v => setAuditCfg(c => ({ ...c, channel_id: v || null }))}
                    >
                      <SelectTrigger data-testid="audit-channel-select" className="bg-[#06070A] border-white/10 rounded-none">
                        <SelectValue placeholder="Pick a channel" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0D0E15] border-white/10 rounded-none">
                        {textChannels.map(c => (
                          <SelectItem key={c.id} value={c.id}>#{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Enabled</label>
                    <button
                      data-testid="audit-toggle-btn"
                      onClick={() => setAuditCfg(c => ({ ...c, enabled: !c.enabled }))}
                      className={`w-full py-2.5 border font-mono text-xs tracking-widest transition-all ${auditCfg.enabled ? "bg-[#10B981] text-black border-[#10B981] font-bold" : "border-white/10 text-[#94A3B8] hover:border-white/30"}`}
                    >{auditCfg.enabled ? "ON" : "OFF"}</button>
                  </div>
                  <div className="flex items-end gap-2">
                    <Button data-testid="audit-save-btn" onClick={saveAuditCfg} className="rounded-none bg-[#10B981] text-black hover:bg-[#10B981]/80 font-bold flex-1">
                      <Save className="w-4 h-4 mr-2" /> SAVE
                    </Button>
                    <Button data-testid="audit-test-btn" onClick={testAuditCfg} variant="outline" className="rounded-none border-white/10 hover:border-[#8B5CF6] hover:text-[#8B5CF6]">
                      TEST
                    </Button>
                    <Button
                      data-testid="audit-export-csv-btn"
                      onClick={exportAuditCsv}
                      variant="outline"
                      disabled={!proStatus?.is_pro}
                      title={proStatus?.is_pro ? "Export last 2000 mod actions as CSV" : "CSV export is a ModNode+ feature"}
                      className={`rounded-none ${proStatus?.is_pro ? "border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/10" : "border-white/10 text-[#64748B] cursor-not-allowed"}`}
                    >
                      {proStatus?.is_pro ? "CSV ✨" : "CSV 🔒"}
                    </Button>
                  </div>
                </div>

                <div>
                  <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2">Events To Route · {(auditCfg.events || []).length}/{auditEvents.length}</div>
                  <div className="flex flex-wrap gap-2" data-testid="audit-events">
                    {auditEvents.map(evt => {
                      const active = (auditCfg.events || []).includes(evt);
                      return (
                        <button
                          key={evt}
                          data-testid={`audit-event-${evt}`}
                          onClick={() => toggleAuditEvent(evt)}
                          className={`px-2.5 py-1 border font-mono text-[10px] tracking-widest transition-all ${active ? "bg-[#8B5CF6]/10 border-[#8B5CF6] text-[#8B5CF6]" : "border-white/10 text-[#64748B] hover:border-white/30 hover:text-[#94A3B8]"}`}
                        >{evt.toUpperCase()}</button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Live audit stream */}
              <div className="border border-white/10 bg-black scanlines" data-testid="audit-terminal">
                <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                  <Radio className="w-3.5 h-3.5 text-[#10B981]" />
                  <span className="font-mono text-[10px] tracking-widest text-[#10B981] uppercase">Discord Audit Stream · Live</span>
                  <span className="ml-auto font-mono text-[10px] text-[#64748B]">{(audit?.audit_log_entries || []).length} entries</span>
                </div>
                <div className="p-4 max-h-[60vh] overflow-y-auto font-mono text-xs space-y-1">
                  {(audit?.audit_log_entries || []).length === 0 && (
                    <div className="text-[#64748B]">// no audit entries</div>
                  )}
                  {(audit?.audit_log_entries || []).map((e, i) => {
                    const label = ACTION_LABELS[e.action_type] || `ACTION_${e.action_type}`;
                    const isBan = e.action_type === 22;
                    const isKick = e.action_type === 20;
                    const isDel = e.action_type === 72 || e.action_type === 73;
                    const color = isBan ? "text-[#EF4444]" : isKick ? "text-[#F59E0B]" : isDel ? "text-[#EF4444]" : "text-[#8B5CF6]";
                    const usr = audit.users?.find(u => u.id === e.user_id);
                    return (
                      <div key={e.id || i} className="flex gap-3 py-1 border-b border-white/5">
                        <span className="text-[#64748B]">[{String(i).padStart(3,'0')}]</span>
                        <span className={color}>{label}</span>
                        <span className="text-[#94A3B8]">by @{usr?.username || e.user_id}</span>
                        <span className="text-[#64748B]">→ target:{e.target_id || "-"}</span>
                        {e.reason && <span className="text-[#94A3B8]">// {e.reason}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {section === "cmds" && (
            <div className="space-y-4" data-testid="cmds-panel">
              <div className="border border-white/10 bg-[#0D0E15] p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Terminal className="w-4 h-4 text-[#10B981]" />
                  <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Mod Commands · Role Access</span>
                </div>
                <p className="text-xs text-[#94A3B8]">Run any mod action from here and pick which Discord role is allowed to use each command. Admins/owners can always run all commands.</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {cmdPerms.map(cmd => (
                  <CmdCard
                    key={cmd.key}
                    cmd={cmd}
                    roles={roles}
                    channels={textChannels}
                    form={cmdForms[cmd.key] || {}}
                    setForm={setForm}
                    savePerm={saveCmdPerm}
                    onRun={() => runCmd(cmd.key)}
                  />
                ))}
              </div>
            </div>
          )}

          {section === "verify" && (
            <div className="space-y-4" data-testid="verify-section">
              <div className="border border-[#10B981]/25 bg-[#0D0E15] p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-[#10B981]" />
                    <span className="font-mono text-[10px] tracking-widest text-[#10B981] uppercase">Verification Gate</span>
                  </div>
                  {verifyCfg?.sent_message_id && (
                    <span className="font-mono text-[10px] text-[#10B981]">◉ Live · msg {verifyCfg.sent_message_id.slice(-6)}</span>
                  )}
                </div>
                {!verifyCfg && <div className="font-mono text-xs text-[#64748B]">// loading…</div>}
                {verifyCfg && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Post Verification In</label>
                      <select
                        data-testid="verify-channel-select"
                        value={verifyCfg.channel_id || ""}
                        onChange={e => saveVerifyCfg({ channel_id: e.target.value || null })}
                        className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#10B981] font-mono"
                      >
                        <option value="">— pick a channel —</option>
                        {channels.filter(c => c.type === 0).map(c => (
                          <option key={c.id} value={c.id}>#{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Role Granted On Verify</label>
                      <select
                        data-testid="verify-role-select"
                        value={verifyCfg.verified_role_id || ""}
                        onChange={e => saveVerifyCfg({ verified_role_id: e.target.value || null })}
                        className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#10B981] font-mono"
                      >
                        <option value="">— pick a role —</option>
                        {roles.filter(r => r.id !== guild?.id && !r.managed).sort((a,b)=>(b.position||0)-(a.position||0)).map(r => (
                          <option key={r.id} value={r.id}>@{r.name}</option>
                        ))}
                      </select>
                      <div className="mt-1 font-mono text-[10px] text-[#64748B]">
                        // bot's highest role must be ABOVE this role for the grant to succeed
                      </div>
                    </div>
                    <div>
                      <label className="font-mono text-[10px] tracking-widest text-[#EF4444] uppercase mb-2 block">Role REMOVED On Verify · optional</label>
                      <select
                        data-testid="verify-unverified-role-select"
                        value={verifyCfg.unverified_role_id || ""}
                        onChange={e => saveVerifyCfg({ unverified_role_id: e.target.value || null })}
                        className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#EF4444] font-mono"
                      >
                        <option value="">— none · leave roles alone —</option>
                        {roles.filter(r => r.id !== guild?.id && !r.managed && r.id !== verifyCfg.verified_role_id).sort((a,b)=>(b.position||0)-(a.position||0)).map(r => (
                          <option key={r.id} value={r.id}>@{r.name}</option>
                        ))}
                      </select>
                      <div className="mt-1 font-mono text-[10px] text-[#64748B]">
                        // e.g. an "@Unverified" holding-pen role members get on join — stripped after verify succeeds
                      </div>
                    </div>
                    <div>
                      <label className="font-mono text-[10px] tracking-widest text-[#F59E0B] uppercase mb-2 block flex items-center gap-1.5">
                        <ShieldCheck className="w-3 h-3" /> Vanity Verify Link · ModNode+ only
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-[#64748B] bg-[#06070A] border border-white/10 px-2 py-2 whitespace-nowrap">/verify/</span>
                        <input
                          data-testid="verify-vanity-slug"
                          value={verifyCfg.vanity_slug || ""}
                          onChange={e => setVerifyCfg(c => ({ ...c, vanity_slug: e.target.value }))}
                          onBlur={e => saveVerifyCfg({ vanity_slug: e.target.value.trim() || null })}
                          placeholder={proStatus?.is_pro ? "myserver" : "ModNode+ required"}
                          disabled={!proStatus?.is_pro}
                          maxLength={32}
                          className="flex-1 bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#F59E0B] font-mono disabled:opacity-50"
                        />
                      </div>
                      <div className="mt-1 font-mono text-[10px] text-[#64748B]">
                        {proStatus?.is_pro
                          ? "// 3-32 chars · lowercase letters, numbers, dashes · must be unique"
                          : "// upgrade to ModNode+ to replace the long snowflake ID with a short vanity URL"}
                      </div>
                    </div>
                    <div>
                      <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Title</label>
                      <input
                        data-testid="verify-title"
                        value={verifyCfg.title || ""}
                        onChange={e => setVerifyCfg(c => ({ ...c, title: e.target.value }))}
                        onBlur={e => saveVerifyCfg({ title: e.target.value })}
                        className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#10B981] font-mono"
                      />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Button Label</label>
                      <input
                        data-testid="verify-button-label"
                        value={verifyCfg.button_label || ""}
                        onChange={e => setVerifyCfg(c => ({ ...c, button_label: e.target.value }))}
                        onBlur={e => saveVerifyCfg({ button_label: e.target.value })}
                        maxLength={80}
                        className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#10B981] font-mono"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Body</label>
                      <textarea
                        data-testid="verify-body"
                        value={verifyCfg.body || ""}
                        onChange={e => setVerifyCfg(c => ({ ...c, body: e.target.value }))}
                        onBlur={e => saveVerifyCfg({ body: e.target.value })}
                        rows={3}
                        className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#10B981] font-mono resize-y"
                      />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Success DM Text</label>
                      <input
                        data-testid="verify-success-msg"
                        value={verifyCfg.success_message || ""}
                        onChange={e => setVerifyCfg(c => ({ ...c, success_message: e.target.value }))}
                        onBlur={e => saveVerifyCfg({ success_message: e.target.value })}
                        className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#10B981] font-mono"
                      />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Already-Verified Text</label>
                      <input
                        data-testid="verify-already-msg"
                        value={verifyCfg.already_verified_message || ""}
                        onChange={e => setVerifyCfg(c => ({ ...c, already_verified_message: e.target.value }))}
                        onBlur={e => saveVerifyCfg({ already_verified_message: e.target.value })}
                        className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#10B981] font-mono"
                      />
                    </div>
                  </div>
                )}
              </div>

              {verifyCfg && <VerifyGuards guildId={guildId} cfg={verifyCfg} onSave={saveVerifyCfg} busy={verifyBusy} />}

              {/* Preview */}
              {verifyCfg && (
                <div className="border border-white/10 bg-[#06070A] p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-3.5 h-3.5 text-[#8B5CF6]" />
                    <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Preview</span>
                  </div>
                  <div className="border-l-4 border-[#10B981] pl-4 py-3 bg-[#0D0E15] rounded-r-md">
                    <div className="font-display font-bold text-lg text-white mb-2">{verifyCfg.title || "Verify to Access"}</div>
                    <div className="text-sm text-[#E2E8F0] whitespace-pre-wrap">{verifyCfg.body || ""}</div>
                    <button
                      disabled
                      className="mt-3 px-4 py-2 bg-[#10B981] text-white font-display font-bold text-sm rounded flex items-center gap-2 opacity-90 cursor-default"
                    >
                      ✅ {verifyCfg.button_label || "Verify Me"}
                    </button>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <button
                  data-testid="verify-send-btn"
                  onClick={sendVerifyPrompt}
                  disabled={verifyBusy || !verifyCfg?.channel_id || !verifyCfg?.verified_role_id}
                  className="px-5 py-2.5 bg-[#10B981] hover:bg-[#1F8B4C] text-white font-display font-bold text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Play className="w-3.5 h-3.5" />
                  {verifyCfg?.sent_message_id ? "Repost Verification Prompt" : "Post Verification Prompt"}
                </button>
                {verifyCfg?.sent_channel_id && verifyCfg?.sent_message_id && (
                  <a
                    href={`https://discord.com/channels/${guildId}/${verifyCfg.sent_channel_id}/${verifyCfg.sent_message_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-5 py-2.5 border border-[#8B5CF6]/40 text-[#8B5CF6] hover:bg-[#8B5CF6]/10 font-display font-bold text-sm flex items-center gap-2"
                    data-testid="verify-jump-link"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                    Jump to Live Prompt
                  </a>
                )}
              </div>
              <div className="font-mono text-[10px] text-[#64748B]">
                // clicking Post creates a new Components-V2 message with a green "Verify" button · new members who click it will receive the role above
              </div>
            </div>
          )}

          {section === "warns" && (
            <div className="border border-white/10 bg-[#0D0E15]">
              <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-[#F59E0B]" />
                <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Warnings · {warnings.length}</span>
              </div>
              <div className="max-h-[70vh] overflow-y-auto" data-testid="warnings-list">
                {warnings.length === 0 && <div className="p-8 text-center font-mono text-xs text-[#64748B]">// no warnings issued</div>}
                {warnings.map(w => (
                  <div key={w.id} className="p-4 border-b border-white/5 flex items-start gap-3">
                    <AlertTriangle className="w-4 h-4 text-[#F59E0B] mt-0.5" />
                    <div className="flex-1">
                      <div className="text-sm"><span className="font-medium">{w.username || w.user_id}</span> <span className="text-[#64748B]">·</span> <span className="text-[#94A3B8]">{w.reason}</span></div>
                      <div className="font-mono text-[10px] text-[#64748B] mt-1">by @{w.moderator_name || w.moderator_id} · {new Date(w.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {section === "actions" && (
            <div className="border border-white/10 bg-black scanlines">
              <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                <Zap className="w-3.5 h-3.5 text-[#10B981]" />
                <span className="font-mono text-[10px] tracking-widest text-[#10B981] uppercase">ModNode Action Log</span>
              </div>
              <div className="p-4 max-h-[70vh] overflow-y-auto font-mono text-xs space-y-1" data-testid="actions-list">
                {actions.length === 0 && <div className="text-[#64748B]">// no actions logged yet</div>}
                {actions.map(a => (
                  <div key={a.id} className="flex gap-3 py-1 border-b border-white/5">
                    <span className="text-[#64748B]">{new Date(a.created_at).toLocaleTimeString([], {hour12: false})}</span>
                    <span className={`${a.action==='ban'||a.action==='lockdown'?'text-[#EF4444]': a.action==='timeout'||a.action==='kick'?'text-[#F59E0B]':'text-[#10B981]'} uppercase`}>{a.action}</span>
                    <span className="text-[#94A3B8]">target:{a.target?.slice(0,18)}</span>
                    {a.reason && <span className="text-[#64748B] truncate">// {a.reason}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {section === "pro" && (
            <div className="space-y-4" data-testid="pro-settings-section">
              {/* Header: Pro status */}
              <div className="border border-[#F59E0B]/40 bg-gradient-to-br from-[#0D0E15] to-[#06070A] p-6 relative overflow-hidden">
                <div className="absolute right-0 top-0 w-32 h-32 bg-[#F59E0B]/10 blur-3xl pointer-events-none" />
                <div className="flex items-center gap-3 mb-3">
                  <Crown className="w-5 h-5 text-[#F59E0B]" />
                  <span className="font-display font-black text-2xl tracking-widest text-white">ModNode<span className="text-[#F59E0B]">+</span></span>
                  {proStatus?.is_pro ? (
                    <span className="ml-auto px-3 py-1 border border-[#F59E0B]/50 bg-[#F59E0B]/10 text-[#F59E0B] font-mono text-[10px] tracking-widest">◉ ACTIVE</span>
                  ) : (
                    <span className="ml-auto px-3 py-1 border border-white/10 bg-white/5 text-[#64748B] font-mono text-[10px] tracking-widest">◯ LOCKED</span>
                  )}
                </div>
                <p className="text-xs text-[#94A3B8] max-w-xl">
                  {proStatus?.is_pro
                    ? "This server has ModNode+ unlocked. Customize your brand color, set a welcome message that fires on member join, and export mod logs as CSV."
                    : "Ask a founder to grant your server ModNode+ to unlock custom embed colors, welcome messages, CSV audit exports, priority AI processing, and premium AI models."}
                </p>
              </div>

              {!proSettingsLoaded ? (
                <div className="border border-white/10 bg-[#0D0E15] p-6 text-xs text-[#64748B] font-mono tracking-widest">Loading…</div>
              ) : (
                <>
                  {/* Brand Color */}
                  <div className="border border-white/10 bg-[#0D0E15] p-5" data-testid="pro-brand-color-panel">
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles className="w-4 h-4 text-[#F59E0B]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Brand Embed Color</span>
                    </div>
                    <p className="text-xs text-[#94A3B8] mb-4">
                      Pick a color used on the verify prompt, audit log embeds, and welcome messages. Non-Pro guilds get ModNode cyan.
                    </p>
                    <div className="flex items-center gap-4 flex-wrap">
                      <input
                        data-testid="pro-color-picker"
                        type="color"
                        disabled={!proStatus?.is_pro}
                        value={"#" + ((proSettings?.embed_color ?? 0xF0B232).toString(16).padStart(6, "0"))}
                        onChange={e => setProSettings(s => ({ ...s, embed_color: parseInt(e.target.value.replace("#", ""), 16) }))}
                        className="w-20 h-14 bg-transparent border border-white/10 cursor-pointer disabled:cursor-not-allowed"
                      />
                      <div className="font-mono text-xs text-[#94A3B8]">
                        <div className="text-[10px] tracking-widest text-[#64748B] uppercase">Hex</div>
                        <div className="font-display font-bold text-lg text-[#F59E0B]">
                          #{((proSettings?.embed_color ?? 0xF0B232).toString(16).padStart(6, "0")).toUpperCase()}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-auto">
                        {[0xF0B232, 0x5865F2, 0x23A55A, 0xDA373C, 0x00F0FF, 0xEB459E].map(c => (
                          <button
                            key={c}
                            disabled={!proStatus?.is_pro}
                            data-testid={`pro-color-preset-${c.toString(16)}`}
                            onClick={() => setProSettings(s => ({ ...s, embed_color: c }))}
                            style={{ background: "#" + c.toString(16).padStart(6, "0") }}
                            className="w-8 h-8 border border-white/10 hover:scale-110 transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
                          />
                        ))}
                      </div>
                      <Button
                        data-testid="pro-color-save-btn"
                        disabled={!proStatus?.is_pro || proSettingsBusy}
                        onClick={() => saveProSettings({ embed_color: proSettings?.embed_color ?? 0xF0B232 })}
                        className="rounded-none bg-[#F59E0B] text-black hover:bg-[#F59E0B]/80 font-bold"
                      >
                        <Save className="w-4 h-4 mr-2" /> SAVE COLOR
                      </Button>
                    </div>
                  </div>

                  {/* Welcome Messages */}
                  <div className="border border-white/10 bg-[#0D0E15] p-5" data-testid="pro-welcome-panel">
                    <div className="flex items-center gap-2 mb-4">
                      <UsersRound className="w-4 h-4 text-[#F59E0B]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Welcome Messages</span>
                      <span className={`ml-auto flex items-center gap-1 px-2 py-1 font-mono text-[10px] tracking-widest ${proSettings?.welcome_enabled ? "bg-[#F59E0B]/10 border border-[#F59E0B]/40 text-[#F59E0B]" : "bg-white/5 border border-white/10 text-[#64748B]"}`}>
                        <span className={`w-1.5 h-1.5 ${proSettings?.welcome_enabled ? "bg-[#F59E0B] pulse-dot" : "bg-[#64748B]"}`} />
                        {proSettings?.welcome_enabled ? "LIVE" : "DISABLED"}
                      </span>
                    </div>
                    <p className="text-xs text-[#94A3B8] mb-4">
                      Posts a branded welcome card in your chosen channel every time a new member joins. Use <code className="text-[#F59E0B]">{"{user}"}</code>, <code className="text-[#F59E0B]">{"{guild}"}</code>, <code className="text-[#F59E0B]">{"{count}"}</code>, <code className="text-[#F59E0B]">{"{mention}"}</code> as placeholders.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Channel</label>
                        <Select
                          value={proSettings?.welcome_channel_id || ""}
                          onValueChange={v => setProSettings(s => ({ ...s, welcome_channel_id: v || null }))}
                        >
                          <SelectTrigger data-testid="pro-welcome-channel-select" disabled={!proStatus?.is_pro} className="bg-[#06070A] border-white/10 rounded-none">
                            <SelectValue placeholder="Pick a channel" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0D0E15] border-white/10 rounded-none">
                            {textChannels.map(c => (
                              <SelectItem key={c.id} value={c.id}>#{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Enabled</label>
                        <button
                          data-testid="pro-welcome-toggle-btn"
                          disabled={!proStatus?.is_pro}
                          onClick={() => setProSettings(s => ({ ...s, welcome_enabled: !s?.welcome_enabled }))}
                          className={`w-full py-2.5 border font-mono text-xs tracking-widest transition-all ${proSettings?.welcome_enabled ? "bg-[#F59E0B] text-black border-[#F59E0B] font-bold" : "border-white/10 text-[#94A3B8] hover:border-white/30"} disabled:opacity-40 disabled:cursor-not-allowed`}
                        >{proSettings?.welcome_enabled ? "ON" : "OFF"}</button>
                      </div>
                      <div>
                        <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Mention Member</label>
                        <button
                          data-testid="pro-welcome-mention-btn"
                          disabled={!proStatus?.is_pro}
                          onClick={() => setProSettings(s => ({ ...s, welcome_mention: !s?.welcome_mention }))}
                          className={`w-full py-2.5 border font-mono text-xs tracking-widest transition-all ${proSettings?.welcome_mention ? "bg-[#8B5CF6] text-white border-[#8B5CF6] font-bold" : "border-white/10 text-[#94A3B8] hover:border-white/30"} disabled:opacity-40 disabled:cursor-not-allowed`}
                        >{proSettings?.welcome_mention ? "PING ON" : "PING OFF"}</button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Title</label>
                        <Input
                          data-testid="pro-welcome-title-input"
                          disabled={!proStatus?.is_pro}
                          value={proSettings?.welcome_title || ""}
                          onChange={e => setProSettings(s => ({ ...s, welcome_title: e.target.value }))}
                          placeholder="Welcome, {user}"
                          className="bg-[#06070A] border-white/10 rounded-none"
                        />
                      </div>
                      <div>
                        <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Image URL (optional)</label>
                        <Input
                          data-testid="pro-welcome-image-input"
                          disabled={!proStatus?.is_pro}
                          value={proSettings?.welcome_image_url || ""}
                          onChange={e => setProSettings(s => ({ ...s, welcome_image_url: e.target.value }))}
                          placeholder="https://..."
                          className="bg-[#06070A] border-white/10 rounded-none"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Body</label>
                      <Textarea
                        data-testid="pro-welcome-body-input"
                        disabled={!proStatus?.is_pro}
                        value={proSettings?.welcome_body || ""}
                        onChange={e => setProSettings(s => ({ ...s, welcome_body: e.target.value }))}
                        rows={4}
                        placeholder="Welcome to {guild}! You're member #{count}. Grab your roles and say hi!"
                        className="bg-[#06070A] border-white/10 rounded-none font-mono text-xs"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <Input
                        data-testid="pro-welcome-footer-input"
                        disabled={!proStatus?.is_pro}
                        value={proSettings?.footer_text || ""}
                        onChange={e => setProSettings(s => ({ ...s, footer_text: e.target.value }))}
                        placeholder="Footer suffix (optional) · appears on all Pro embeds"
                        className="bg-[#06070A] border-white/10 rounded-none flex-1"
                      />
                      <Button
                        data-testid="pro-welcome-save-btn"
                        disabled={!proStatus?.is_pro || proSettingsBusy}
                        onClick={() => saveProSettings({
                          welcome_enabled: !!proSettings?.welcome_enabled,
                          welcome_channel_id: proSettings?.welcome_channel_id || null,
                          welcome_title: proSettings?.welcome_title || "",
                          welcome_body: proSettings?.welcome_body || "",
                          welcome_mention: !!proSettings?.welcome_mention,
                          welcome_image_url: proSettings?.welcome_image_url || "",
                          footer_text: proSettings?.footer_text || "",
                        })}
                        className="rounded-none bg-[#F59E0B] text-black hover:bg-[#F59E0B]/80 font-bold"
                      >
                        <Save className="w-4 h-4 mr-2" /> SAVE WELCOME
                      </Button>
                    </div>
                  </div>

                  {/* Priority AI badge */}
                  <div className="border border-white/10 bg-[#0D0E15] p-5" data-testid="pro-priority-panel">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-4 h-4 text-[#F59E0B]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Priority AI Processing</span>
                      <span className={`ml-auto px-2 py-1 font-mono text-[10px] tracking-widest ${proStatus?.is_pro ? "bg-[#F59E0B]/10 border border-[#F59E0B]/40 text-[#F59E0B]" : "bg-white/5 border border-white/10 text-[#64748B]"}`}>
                        {proStatus?.is_pro ? "ENABLED" : "LOCKED"}
                      </span>
                    </div>
                    <p className="text-xs text-[#94A3B8]">
                      Pro guilds use premium AI models (GPT-5.4, Claude Opus, Gemini 3.1 Pro) with a wider budget and extended audit retention of up to {proStatus?.perks?.audit_retention_days || 30} days.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                      <div className="border border-white/5 bg-[#06070A] p-3">
                        <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">Custom Words</div>
                        <div className="font-display font-bold text-lg text-[#F59E0B]">{proStatus?.perks?.custom_words_max || 25}</div>
                      </div>
                      <div className="border border-white/5 bg-[#06070A] p-3">
                        <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">Trusted Roles</div>
                        <div className="font-display font-bold text-lg text-[#F59E0B]">{proStatus?.perks?.trusted_roles_max || 5}</div>
                      </div>
                      <div className="border border-white/5 bg-[#06070A] p-3">
                        <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">Excluded Chans</div>
                        <div className="font-display font-bold text-lg text-[#F59E0B]">{proStatus?.perks?.excluded_channels_max || 5}</div>
                      </div>
                      <div className="border border-white/5 bg-[#06070A] p-3">
                        <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">Max Severity</div>
                        <div className="font-display font-bold text-lg text-[#F59E0B] uppercase">{proStatus?.perks?.max_action_severity_cap || "high"}</div>
                      </div>
                    </div>
                  </div>

                  {/* Security Health Report */}
                  <div className="border border-white/10 bg-[#0D0E15] p-5" data-testid="pro-security-health-panel">
                    <div className="flex items-center gap-2 mb-4">
                      <ShieldCheck className="w-4 h-4 text-[#F59E0B]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Security Health Report</span>
                      <Button data-testid="sec-health-refresh" disabled={!proStatus?.is_pro || secHealthBusy} onClick={loadSecurityHealth} variant="outline" size="sm" className="ml-auto rounded-none border-white/10 hover:border-[#F59E0B] hover:text-[#F59E0B]">
                        <RefreshCcw className="w-3 h-3 mr-1" /> RE-SCAN
                      </Button>
                    </div>
                    {!proStatus?.is_pro ? (
                      <div className="font-mono text-xs text-[#64748B]">// Security Health Reports are a ModNode+ feature.</div>
                    ) : !secHealth ? (
                      <div className="font-mono text-xs text-[#64748B]">// Loading…</div>
                    ) : (
                      <>
                        <div className="grid grid-cols-3 gap-3 mb-4">
                          <div className="border border-white/5 bg-[#06070A] p-4 text-center">
                            <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">Grade</div>
                            <div className={`font-display font-black text-5xl ${secHealth.grade === "A" ? "text-[#10B981]" : secHealth.grade === "B" ? "text-[#8B5CF6]" : secHealth.grade === "C" ? "text-[#F59E0B]" : "text-[#EF4444]"}`}>{secHealth.grade}</div>
                          </div>
                          <div className="border border-white/5 bg-[#06070A] p-4 text-center">
                            <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">Score</div>
                            <div className="font-display font-black text-5xl text-[#F59E0B]">{secHealth.score}<span className="text-xl text-[#64748B]">/{secHealth.max_score}</span></div>
                          </div>
                          <div className="border border-white/5 bg-[#06070A] p-4 text-center">
                            <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">Checks</div>
                            <div className="font-mono text-xs text-[#94A3B8] mt-2">
                              <div><span className="text-[#10B981]">● {secHealth.summary.passing}</span> passing</div>
                              <div><span className="text-[#F59E0B]">● {secHealth.summary.warnings}</span> warnings</div>
                              <div><span className="text-[#EF4444]">● {secHealth.summary.failing}</span> failing</div>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-1.5" data-testid="sec-health-checks">
                          {secHealth.checks.map(c => (
                            <div key={c.key} className="flex items-start gap-3 border border-white/5 bg-[#06070A] p-3">
                              <span className={`mt-1 w-2 h-2 shrink-0 ${c.status === "pass" ? "bg-[#10B981]" : c.status === "warn" ? "bg-[#F59E0B]" : c.status === "fail" ? "bg-[#EF4444]" : "bg-[#64748B]"}`} />
                              <div className="flex-1 min-w-0">
                                <div className="font-mono text-xs text-white">{c.label}</div>
                                <div className="font-mono text-[10px] text-[#64748B] mt-0.5">{c.detail}</div>
                              </div>
                              <span className={`font-mono text-[10px] tracking-widest uppercase ${c.status === "pass" ? "text-[#10B981]" : c.status === "warn" ? "text-[#F59E0B]" : c.status === "fail" ? "text-[#EF4444]" : "text-[#64748B]"}`}>{c.status}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Premium Usage Statistics */}
                  <div className="border border-white/10 bg-[#0D0E15] p-5" data-testid="pro-usage-stats-panel">
                    <div className="flex items-center gap-2 mb-4">
                      <Activity className="w-4 h-4 text-[#F59E0B]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Premium Usage Statistics</span>
                      <Select value={String(usageDays)} onValueChange={v => { const n = parseInt(v, 10); setUsageDays(n); loadUsageStats(n); }}>
                        <SelectTrigger data-testid="usage-range-select" className="ml-auto w-32 bg-[#06070A] border-white/10 rounded-none">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0D0E15] border-white/10 rounded-none">
                          <SelectItem value="7">7 days</SelectItem>
                          <SelectItem value="14">14 days</SelectItem>
                          <SelectItem value="30">30 days</SelectItem>
                          <SelectItem value="60">60 days</SelectItem>
                          <SelectItem value="90">90 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {!proStatus?.is_pro ? (
                      <div className="font-mono text-xs text-[#64748B]">// Premium Usage Statistics are a ModNode+ feature.</div>
                    ) : !usageStats ? (
                      <div className="font-mono text-xs text-[#64748B]">// Loading…</div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                          <div className="border border-white/5 bg-[#06070A] p-3">
                            <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">Actions</div>
                            <div className="font-display font-bold text-2xl text-[#F59E0B]">{usageStats.totals.actions}</div>
                          </div>
                          <div className="border border-white/5 bg-[#06070A] p-3">
                            <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">Warnings</div>
                            <div className="font-display font-bold text-2xl text-[#F59E0B]">{usageStats.totals.warnings}</div>
                          </div>
                          <div className="border border-white/5 bg-[#06070A] p-3">
                            <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">Verifications</div>
                            <div className="font-display font-bold text-2xl text-[#F59E0B]">{usageStats.totals.verifications}</div>
                          </div>
                          <div className="border border-white/5 bg-[#06070A] p-3">
                            <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">Mods</div>
                            <div className="font-display font-bold text-2xl text-[#F59E0B]">{usageStats.totals.unique_moderators}</div>
                          </div>
                          <div className="border border-white/5 bg-[#06070A] p-3">
                            <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">Targets</div>
                            <div className="font-display font-bold text-2xl text-[#F59E0B]">{usageStats.totals.unique_targets}</div>
                          </div>
                        </div>
                        {/* Sparkline — simple bar chart of daily actions */}
                        <div className="border border-white/5 bg-[#06070A] p-4 mb-4">
                          <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-2">Actions · last {usageStats.range_days} days</div>
                          <div className="flex items-end gap-0.5 h-24" data-testid="usage-sparkline">
                            {usageStats.series.map((d, i) => {
                              const max = Math.max(1, ...usageStats.series.map(x => x.actions));
                              const h = Math.max(2, Math.round((d.actions / max) * 96));
                              return (
                                <div key={i} title={`${d.date}: ${d.actions} actions`} className="flex-1 bg-[#F59E0B] hover:bg-[#F59E0B]/80 transition-colors" style={{ height: `${h}px`, minWidth: 2 }} />
                              );
                            })}
                          </div>
                        </div>
                        {/* Action breakdown + top mods */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="border border-white/5 bg-[#06070A] p-3">
                            <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-2">Action Breakdown</div>
                            <div className="space-y-1 font-mono text-xs" data-testid="usage-breakdown">
                              {usageStats.action_breakdown.length === 0 && <div className="text-[#64748B]">// no actions</div>}
                              {usageStats.action_breakdown.slice(0, 10).map(a => (
                                <div key={a.action} className="flex items-center gap-2">
                                  <span className="text-[#94A3B8] flex-1 truncate">{a.action}</span>
                                  <span className="text-[#F59E0B] font-bold">{a.count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="border border-white/5 bg-[#06070A] p-3">
                            <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-2">Top Moderators</div>
                            <div className="space-y-1 font-mono text-xs" data-testid="usage-top-mods">
                              {usageStats.top_moderators.length === 0 && <div className="text-[#64748B]">// no mod activity</div>}
                              {usageStats.top_moderators.slice(0, 8).map(m => (
                                <div key={m.moderator_id} className="flex items-center gap-2">
                                  <span className="text-[#94A3B8] flex-1 truncate">&lt;@{m.moderator_id}&gt;</span>
                                  <span className="text-[#F59E0B] font-bold">{m.count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Private Owner Reports */}
                  <div className="border border-white/10 bg-[#0D0E15] p-5" data-testid="pro-weekly-report-panel">
                    <div className="flex items-center gap-2 mb-4">
                      <ScrollText className="w-4 h-4 text-[#F59E0B]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Private Owner Report (Weekly)</span>
                      <Button data-testid="weekly-send-btn" disabled={!proStatus?.is_pro || weeklyBusy} onClick={sendWeeklyReport} className="ml-auto rounded-none bg-[#F59E0B] text-black hover:bg-[#F59E0B]/80 font-bold">
                        <Radio className="w-4 h-4 mr-2" /> {weeklyBusy ? "SENDING…" : "DM OWNER NOW"}
                      </Button>
                    </div>
                    <WeeklySchedule guildId={guildId} isPro={!!proStatus?.is_pro} />
                    <p className="text-xs text-[#94A3B8] mb-4">
                      Private 7-day summary DM'd directly to the server owner — moderation, members, raid events, and security grade. The report is also previewed below so admins know exactly what the owner sees.
                    </p>
                    {!proStatus?.is_pro ? (
                      <div className="font-mono text-xs text-[#64748B]">// Private Owner Reports are a ModNode+ feature.</div>
                    ) : !weeklyReport ? (
                      <div className="font-mono text-xs text-[#64748B]">// Loading…</div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="weekly-preview">
                        <div className="border border-white/5 bg-[#06070A] p-3">
                          <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-2">Moderation (7d)</div>
                          <div className="font-mono text-xs space-y-0.5">
                            <div>Actions: <span className="text-[#F59E0B]">{weeklyReport.moderation.actions}</span></div>
                            <div>Bans: <span className="text-[#EF4444]">{weeklyReport.moderation.bans}</span></div>
                            <div>Kicks: <span className="text-[#F59E0B]">{weeklyReport.moderation.kicks}</span></div>
                            <div>Timeouts: <span className="text-[#8B5CF6]">{weeklyReport.moderation.timeouts}</span></div>
                            <div>Warns: <span className="text-[#94A3B8]">{weeklyReport.moderation.warns}</span></div>
                          </div>
                        </div>
                        <div className="border border-white/5 bg-[#06070A] p-3">
                          <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-2">Members (7d)</div>
                          <div className="font-mono text-xs space-y-0.5">
                            <div>Verifications: <span className="text-[#10B981]">{weeklyReport.members.verifications}</span></div>
                            <div>Current: <span className="text-white">{weeklyReport.members.current_count ?? "?"}</span></div>
                            <div>Online: <span className="text-[#10B981]">{weeklyReport.members.presence ?? "?"}</span></div>
                          </div>
                        </div>
                        <div className="border border-white/5 bg-[#06070A] p-3">
                          <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-2">Security</div>
                          <div className="font-mono text-xs space-y-0.5">
                            <div>Grade: <span className="text-[#F59E0B] font-bold">{weeklyReport.security.grade}</span></div>
                            <div>Score: <span className="text-[#F59E0B]">{weeklyReport.security.score}/85</span></div>
                            <div>Warnings: <span className="text-[#F59E0B]">{weeklyReport.security.warnings}</span></div>
                            <div>Failing: <span className="text-[#EF4444]">{weeklyReport.security.failing}</span></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Premium Support Queue */}
                  <div className="border border-white/10 bg-[#0D0E15] p-5" data-testid="pro-support-panel">
                    <div className="flex items-center gap-2 mb-4">
                      <Flame className="w-4 h-4 text-[#F59E0B]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Premium Support Queue</span>
                      <span className="ml-auto px-2 py-1 font-mono text-[10px] tracking-widest bg-white/5 border border-white/10 text-[#64748B]">{tickets.length} open</span>
                    </div>
                    <p className="text-xs text-[#94A3B8] mb-4">
                      Open a priority ticket for ModNode founders. Replies land in your Discord DMs with the moderator-only context attached.
                    </p>
                    {!proStatus?.is_pro ? (
                      <div className="font-mono text-xs text-[#64748B]">// Premium Support is a ModNode+ feature.</div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                          <Input data-testid="ticket-subject-input" value={ticketDraft.subject} onChange={e => setTicketDraft(d => ({ ...d, subject: e.target.value }))} placeholder="Subject" className="md:col-span-3 bg-[#06070A] border-white/10 rounded-none" />
                          <Select value={ticketDraft.priority} onValueChange={v => setTicketDraft(d => ({ ...d, priority: v }))}>
                            <SelectTrigger data-testid="ticket-priority-select" className="bg-[#06070A] border-white/10 rounded-none">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0D0E15] border-white/10 rounded-none">
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="urgent">Urgent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Textarea data-testid="ticket-body-input" value={ticketDraft.body} onChange={e => setTicketDraft(d => ({ ...d, body: e.target.value }))} placeholder="What's going on? Include server ID, affected channels, and reproduction steps." rows={4} className="bg-[#06070A] border-white/10 rounded-none font-mono text-xs mb-3" />
                        <Button data-testid="ticket-submit-btn" disabled={ticketBusy} onClick={createTicket} className="rounded-none bg-[#F59E0B] text-black hover:bg-[#F59E0B]/80 font-bold w-full md:w-auto">
                          {ticketBusy ? "SUBMITTING…" : "SUBMIT TICKET"}
                        </Button>
                        {tickets.length > 0 && (
                          <div className="border-t border-white/10 mt-4 pt-3 space-y-2" data-testid="ticket-list">
                            {tickets.map(t => (
                              <div key={t.id} className="border border-white/5 bg-[#06070A] p-3">
                                <div className="flex items-center gap-2">
                                  <span className={`px-1.5 py-0.5 font-mono text-[9px] tracking-widest ${t.priority === "urgent" ? "bg-[#EF4444]/20 text-[#EF4444]" : t.priority === "high" ? "bg-[#F59E0B]/20 text-[#F59E0B]" : t.priority === "normal" ? "bg-[#8B5CF6]/20 text-[#8B5CF6]" : "bg-white/10 text-[#64748B]"}`}>{t.priority.toUpperCase()}</span>
                                  <span className="font-mono text-xs text-white truncate flex-1">{t.subject}</span>
                                  <span className={`px-1.5 py-0.5 font-mono text-[9px] tracking-widest ${t.status === "closed" ? "bg-white/10 text-[#64748B]" : t.status === "awaiting_user" ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "bg-[#10B981]/20 text-[#10B981]"}`}>{t.status.replace("_", " ").toUpperCase()}</span>
                                </div>
                                <div className="font-mono text-[10px] text-[#64748B] mt-1">
                                  Opened {new Date(t.created_at).toLocaleString()} · {(t.replies || []).length} reply(ies)
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Scheduled Announcements */}
                  <div className="border border-white/10 bg-[#0D0E15] p-5" data-testid="pro-scheduler-panel">
                    <div className="flex items-center gap-2 mb-4">
                      <Clock className="w-4 h-4 text-[#F59E0B]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Scheduled Announcements</span>
                      <span className="ml-auto px-2 py-1 font-mono text-[10px] tracking-widest bg-white/5 border border-white/10 text-[#64748B]">
                        {schedules.length} queued
                      </span>
                    </div>
                    <p className="text-xs text-[#94A3B8] mb-4">
                      Queue V2 embed messages to post at a future time. Supports one-off, daily, or weekly recurrence.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <Select value={scheduleDraft.channel_id} onValueChange={v => setScheduleDraft(s => ({ ...s, channel_id: v }))}>
                        <SelectTrigger data-testid="sched-channel-select" disabled={!proStatus?.is_pro} className="bg-[#06070A] border-white/10 rounded-none">
                          <SelectValue placeholder="Target channel" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0D0E15] border-white/10 rounded-none">
                          {textChannels.map(c => (<SelectItem key={c.id} value={c.id}>#{c.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <Input type="datetime-local" data-testid="sched-when-input" disabled={!proStatus?.is_pro} value={scheduleDraft.send_at} onChange={e => setScheduleDraft(s => ({ ...s, send_at: e.target.value }))} className="bg-[#06070A] border-white/10 rounded-none" />
                    </div>
                    <Input data-testid="sched-title-input" disabled={!proStatus?.is_pro} value={scheduleDraft.title} onChange={e => setScheduleDraft(s => ({ ...s, title: e.target.value }))} placeholder="Announcement title" className="bg-[#06070A] border-white/10 rounded-none mb-3" />
                    <Textarea data-testid="sched-body-input" disabled={!proStatus?.is_pro} value={scheduleDraft.body} onChange={e => setScheduleDraft(s => ({ ...s, body: e.target.value }))} placeholder="Body (supports Markdown)" rows={3} className="bg-[#06070A] border-white/10 rounded-none font-mono text-xs mb-3" />
                    <div className="flex items-center gap-2 mb-3">
                      <Select value={scheduleDraft.recurrence} onValueChange={v => setScheduleDraft(s => ({ ...s, recurrence: v }))}>
                        <SelectTrigger data-testid="sched-recurrence-select" disabled={!proStatus?.is_pro} className="bg-[#06070A] border-white/10 rounded-none w-40">
                          <SelectValue placeholder="Recurrence" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0D0E15] border-white/10 rounded-none">
                          <SelectItem value="none">One-off</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input data-testid="sched-image-input" disabled={!proStatus?.is_pro} value={scheduleDraft.image_url} onChange={e => setScheduleDraft(s => ({ ...s, image_url: e.target.value }))} placeholder="Image URL (optional)" className="bg-[#06070A] border-white/10 rounded-none flex-1" />
                      <Button data-testid="sched-create-btn" disabled={!proStatus?.is_pro || scheduleBusy} onClick={createSchedule} className="rounded-none bg-[#F59E0B] text-black hover:bg-[#F59E0B]/80 font-bold">
                        <Clock className="w-4 h-4 mr-2" /> QUEUE
                      </Button>
                    </div>
                    {schedules.length > 0 && (
                      <div className="border-t border-white/10 pt-3 space-y-2" data-testid="sched-list">
                        {schedules.map(s => (
                          <div key={s.id} className="flex items-center gap-2 border border-white/5 bg-[#06070A] p-3">
                            <div className="flex-1 min-w-0">
                              <div className="font-mono text-xs text-[#F59E0B] truncate">{s.title}</div>
                              <div className="font-mono text-[10px] text-[#64748B]">
                                <span className="text-[#8B5CF6]">#{textChannels.find(c => c.id === s.channel_id)?.name || s.channel_id}</span>
                                <span> · {new Date(s.next_run_at).toLocaleString()}</span>
                                <span> · {s.recurrence}</span>
                                <span> · {s.status}</span>
                              </div>
                            </div>
                            <Button data-testid={`sched-cancel-${s.id}`} onClick={() => deleteSchedule(s.id)} variant="outline" size="sm" className="rounded-none border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444]/10">CANCEL</Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Server Snapshots */}
                  <div className="border border-white/10 bg-[#0D0E15] p-5" data-testid="pro-snapshots-panel">
                    <div className="flex items-center gap-2 mb-4">
                      <ScrollText className="w-4 h-4 text-[#F59E0B]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Server Snapshots</span>
                      <Button data-testid="snapshot-take-btn" disabled={!proStatus?.is_pro || snapshotBusy} onClick={takeSnapshot} className="ml-auto rounded-none bg-[#F59E0B] text-black hover:bg-[#F59E0B]/80 font-bold">
                        <Save className="w-4 h-4 mr-2" /> {snapshotBusy ? "SNAPPING…" : "TAKE SNAPSHOT"}
                      </Button>
                    </div>
                    <p className="text-xs text-[#94A3B8] mb-4">
                      Capture every role, channel, permission, and ModNode config as a signed JSON file. Keeps the last 20 per server.
                    </p>
                    {snapshots.length === 0 ? (
                      <div className="font-mono text-xs text-[#64748B]">// no snapshots yet</div>
                    ) : (
                      <div className="space-y-2" data-testid="snapshot-list">
                        {snapshots.map(s => (
                          <div key={s.id} className="flex items-center gap-2 border border-white/5 bg-[#06070A] p-3">
                            <div className="flex-1 min-w-0">
                              <div className="font-mono text-xs text-white">{new Date(s.snapshot_at).toLocaleString()}</div>
                              <div className="font-mono text-[10px] text-[#64748B]">
                                {s.role_count} roles · {s.channel_count} channels · by {s.created_by}
                              </div>
                            </div>
                            <Button data-testid={`snap-dl-${s.id}`} onClick={() => downloadSnapshot(s.id)} variant="outline" size="sm" className="rounded-none border-white/10 hover:border-[#F59E0B] hover:text-[#F59E0B]">DOWNLOAD</Button>
                            <Button data-testid={`snap-del-${s.id}`} onClick={() => deleteSnapshot(s.id)} variant="outline" size="sm" className="rounded-none border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444]/10">DELETE</Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Reaction Roles */}
                  <div className="border border-white/10 bg-[#0D0E15] p-5" data-testid="pro-reaction-roles-panel">
                    <div className="flex items-center gap-2 mb-4">
                      <UsersRound className="w-4 h-4 text-[#F59E0B]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Reaction Roles</span>
                      <span className="ml-auto px-2 py-1 font-mono text-[10px] tracking-widest bg-white/5 border border-white/10 text-[#64748B]">{reactionRoles.length} posted</span>
                    </div>
                    <p className="text-xs text-[#94A3B8] mb-4">
                      Post a message with buttons that members click to self-assign a role. Toggle a role on/off; enable Exclusive mode to make it a 1-of-N pick (color roles, pronouns, timezones).
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <Select value={rrDraft.channel_id} onValueChange={v => setRrDraft(d => ({ ...d, channel_id: v }))}>
                        <SelectTrigger data-testid="rr-channel-select" disabled={!proStatus?.is_pro} className="bg-[#06070A] border-white/10 rounded-none">
                          <SelectValue placeholder="Target channel" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0D0E15] border-white/10 rounded-none">
                          {textChannels.map(c => (<SelectItem key={c.id} value={c.id}>#{c.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <button
                        data-testid="rr-exclusive-toggle"
                        disabled={!proStatus?.is_pro}
                        onClick={() => setRrDraft(d => ({ ...d, exclusive: !d.exclusive }))}
                        className={`py-2.5 px-4 border font-mono text-xs tracking-widest transition-all ${rrDraft.exclusive ? "bg-[#F59E0B] text-black border-[#F59E0B] font-bold" : "border-white/10 text-[#94A3B8] hover:border-white/30"}`}
                      >
                        EXCLUSIVE: {rrDraft.exclusive ? "ON (1 of N)" : "OFF (multi-pick)"}
                      </button>
                    </div>
                    <Input data-testid="rr-title-input" disabled={!proStatus?.is_pro} value={rrDraft.title} onChange={e => setRrDraft(d => ({ ...d, title: e.target.value }))} placeholder="Title" className="bg-[#06070A] border-white/10 rounded-none mb-3" />
                    <Textarea data-testid="rr-body-input" disabled={!proStatus?.is_pro} value={rrDraft.body} onChange={e => setRrDraft(d => ({ ...d, body: e.target.value }))} placeholder="Body" rows={2} className="bg-[#06070A] border-white/10 rounded-none font-mono text-xs mb-3" />
                    <div className="space-y-2 mb-3" data-testid="rr-entries">
                      {rrDraft.roles.map((r, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                          <Select value={r.role_id} onValueChange={v => updateRrEntry(idx, { role_id: v })}>
                            <SelectTrigger data-testid={`rr-role-select-${idx}`} disabled={!proStatus?.is_pro} className="col-span-4 bg-[#06070A] border-white/10 rounded-none">
                              <SelectValue placeholder="Role" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0D0E15] border-white/10 rounded-none max-h-64">
                              {roles.filter(x => x.name !== "@everyone").map(x => (
                                <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input data-testid={`rr-label-${idx}`} disabled={!proStatus?.is_pro} value={r.label} onChange={e => updateRrEntry(idx, { label: e.target.value })} placeholder="Button label" className="col-span-3 bg-[#06070A] border-white/10 rounded-none" />
                          <Input data-testid={`rr-emoji-${idx}`} disabled={!proStatus?.is_pro} value={r.emoji} onChange={e => updateRrEntry(idx, { emoji: e.target.value })} placeholder="emoji (opt)" className="col-span-2 bg-[#06070A] border-white/10 rounded-none" />
                          <Select value={String(r.style || 2)} onValueChange={v => updateRrEntry(idx, { style: parseInt(v, 10) })}>
                            <SelectTrigger data-testid={`rr-style-${idx}`} disabled={!proStatus?.is_pro} className="col-span-2 bg-[#06070A] border-white/10 rounded-none">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0D0E15] border-white/10 rounded-none">
                              <SelectItem value="1">Blurple</SelectItem>
                              <SelectItem value="2">Gray</SelectItem>
                              <SelectItem value="3">Green</SelectItem>
                              <SelectItem value="4">Red</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button data-testid={`rr-remove-${idx}`} disabled={!proStatus?.is_pro || rrDraft.roles.length === 1} onClick={() => removeRrEntry(idx)} variant="outline" size="sm" className="col-span-1 rounded-none border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444]/10">×</Button>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button data-testid="rr-add-entry-btn" disabled={!proStatus?.is_pro || rrDraft.roles.length >= 25} onClick={addRrEntry} variant="outline" className="rounded-none border-white/10 hover:border-[#8B5CF6] hover:text-[#8B5CF6]">
                        + ADD ROLE
                      </Button>
                      <Button data-testid="rr-create-btn" disabled={!proStatus?.is_pro || rrBusy} onClick={createReactionRole} className="ml-auto rounded-none bg-[#F59E0B] text-black hover:bg-[#F59E0B]/80 font-bold">
                        <Play className="w-4 h-4 mr-2" /> {rrBusy ? "POSTING…" : "CREATE + POST"}
                      </Button>
                    </div>
                    {reactionRoles.length > 0 && (
                      <div className="border-t border-white/10 mt-4 pt-3 space-y-2" data-testid="rr-list">
                        {reactionRoles.map(x => (
                          <div key={x.id} className="flex items-center gap-2 border border-white/5 bg-[#06070A] p-3">
                            <div className="flex-1 min-w-0">
                              <div className="font-mono text-xs text-[#F59E0B] truncate">{x.title}</div>
                              <div className="font-mono text-[10px] text-[#64748B]">
                                #{textChannels.find(c => c.id === x.channel_id)?.name || x.channel_id} · {(x.roles || []).length} roles · {x.exclusive ? "exclusive" : "multi"} · {x.message_id ? "posted" : "draft"}
                              </div>
                            </div>
                            <Button data-testid={`rr-repost-${x.id}`} onClick={() => repostReactionRole(x.id)} variant="outline" size="sm" className="rounded-none border-white/10 hover:border-[#F59E0B] hover:text-[#F59E0B]">REPOST</Button>
                            <Button data-testid={`rr-del-${x.id}`} onClick={() => deleteReactionRole(x.id)} variant="outline" size="sm" className="rounded-none border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444]/10">DELETE</Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {section === "bot" && (
            <div className="space-y-4">
              <div className="border border-white/10 bg-[#0D0E15] p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 border border-[#10B981]/50 flex items-center justify-center mm-glow-volt">
                    <Bot className="w-8 h-8 text-[#10B981]" />
                  </div>
                  <div>
                    <div className="font-display font-black text-2xl">MODNODE BOT</div>
                    <div className="font-mono text-xs text-[#94A3B8] tracking-widest">TACTICAL MODERATION ENGINE · v1.0</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { k: "STATUS", v: "ONLINE", c: "text-[#10B981]" },
                    { k: "GUILD ID", v: guild.id, c: "text-white" },
                    { k: "MEMBERS", v: guild.approximate_member_count?.toLocaleString(), c: "text-[#8B5CF6]" },
                    { k: "PRESENCE", v: guild.approximate_presence_count?.toLocaleString(), c: "text-[#8B5CF6]" },
                    { k: "CHANNELS", v: channels.length, c: "text-[#F59E0B]" },
                    { k: "ROLES", v: roles.length, c: "text-[#EF4444]" },
                  ].map(x => (
                    <div key={x.k} className="border border-white/5 p-4 bg-[#06070A]">
                      <div className="font-mono text-[10px] tracking-widest text-[#64748B]">{x.k}</div>
                      <div className={`font-display font-bold text-lg mt-1 ${x.c} truncate`}>{x.v}</div>
                    </div>
                  ))}
                </div>
              </div>

              <SlashSyncCard guildId={guildId} />
            </div>
          )}
        </main>

        {/* RIGHT: Server Control */}
        <aside className="border-l border-white/5 bg-[#08090D] p-4 space-y-4 min-h-[calc(100vh-64px)]">
          <div className="px-2">
            <span className="font-mono text-[10px] tracking-[0.2em] text-[#64748B] uppercase">Server Control</span>
          </div>

          <button
            data-testid="lockdown-btn"
            onClick={() => setConfirmLock('lock')}
            className="w-full group relative bg-[#EF4444] hover:bg-[#DC2626] text-white font-display font-black text-base tracking-tight p-5 mm-glow-danger transition-all duration-200 hover:scale-[1.01]"
          >
            <span className="flex items-center justify-center gap-3">
              <Lock className="w-5 h-5" />
              LOCKDOWN
            </span>
            <span className="block font-mono text-[10px] tracking-widest opacity-70 mt-1">DENIES SEND · ALL CHANNELS</span>
          </button>

          <button
            data-testid="unlock-btn"
            onClick={() => setConfirmLock('unlock')}
            className="w-full border border-[#10B981]/40 hover:border-[#10B981] hover:bg-[#10B981]/5 text-[#10B981] font-display font-bold text-sm tracking-tight p-3 transition-all"
          >
            <span className="flex items-center justify-center gap-2">
              <Unlock className="w-4 h-4" />
              RELEASE LOCKDOWN
            </span>
          </button>

          {/* Raid Mode */}
          <div className="pt-2">
            {stats?.raid_mode_active ? (
              <button
                data-testid="raid-off-btn"
                onClick={() => setConfirmRaid('off')}
                className="w-full group relative bg-[#F59E0B] hover:bg-[#e6a600] text-black font-display font-black text-base tracking-tight p-4 mm-glow-volt transition-all duration-200 hover:scale-[1.01]"
              >
                <span className="flex items-center justify-center gap-3">
                  <Flame className="w-5 h-5" />
                  STAND DOWN RAID MODE
                </span>
                <span className="block font-mono text-[10px] tracking-widest opacity-70 mt-1">CURRENTLY ACTIVE</span>
              </button>
            ) : (
              <button
                data-testid="raid-on-btn"
                onClick={() => setConfirmRaid('on')}
                className="w-full group relative bg-transparent border-2 border-[#F59E0B] hover:bg-[#F59E0B]/10 text-[#F59E0B] font-display font-black text-base tracking-tight p-4 transition-all duration-200 hover:scale-[1.01]"
              >
                <span className="flex items-center justify-center gap-3">
                  <Flame className="w-5 h-5" />
                  ENGAGE RAID MODE
                </span>
                <span className="block font-mono text-[10px] tracking-widest opacity-70 mt-1">VERIFY + SLOWMODE ALL</span>
              </button>
            )}
          </div>

          <div className="border border-white/10 bg-[#0D0E15] p-4">
            <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-3">Quick Facts</div>
            <ul className="space-y-2 text-xs text-[#94A3B8]">
              <li className="flex gap-2"><ChevronRight className="w-3 h-3 text-[#10B981] mt-0.5 shrink-0" /> Search members from MEMBERS tab</li>
              <li className="flex gap-2"><ChevronRight className="w-3 h-3 text-[#10B981] mt-0.5 shrink-0" /> Purge / slowmode under SERVER tab</li>
              <li className="flex gap-2"><ChevronRight className="w-3 h-3 text-[#10B981] mt-0.5 shrink-0" /> Audit stream polls every 15s</li>
              <li className="flex gap-2"><ChevronRight className="w-3 h-3 text-[#10B981] mt-0.5 shrink-0" /> Bot must outrank the target</li>
            </ul>
          </div>

          <div className="px-2 pt-4">
            <span className="font-mono text-[10px] tracking-[0.2em] text-[#64748B] uppercase">Session</span>
            <div className="mt-2 font-mono text-[10px] text-[#64748B]">
              MOD: {user?.username || "—"}<br/>
              GUILD: {guild.id.slice(0,12)}...
            </div>
          </div>
        </aside>
      </div>
      </div>
      </div>

      {/* Action dialog */}
      <Dialog open={!!actionDialog} onOpenChange={(o) => !o && setActionDialog(null)}>
        <DialogContent className="bg-[#0D0E15] border-white/10 rounded-none max-w-md" data-testid="action-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">
              {actionDialog?.type} → @{actionDialog?.member?.user?.username}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {actionDialog?.type === "timeout" && (
              <div>
                <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Duration (minutes)</label>
                <Input data-testid="timeout-minutes-input" type="number" value={timeoutMins} onChange={e => setTimeoutMins(e.target.value)} className="bg-[#06070A] border-white/10 rounded-none" />
              </div>
            )}
            <div>
              <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Reason</label>
              <Textarea data-testid="action-reason-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="Provide a reason..." className="bg-[#06070A] border-white/10 rounded-none" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActionDialog(null)} className="rounded-none">CANCEL</Button>
            <Button data-testid="action-confirm-btn" onClick={runAction} className="rounded-none bg-[#EF4444] hover:bg-[#DC2626] text-white font-bold uppercase">
              CONFIRM {actionDialog?.type}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lockdown confirm */}
      <Dialog open={!!confirmLock} onOpenChange={(o) => !o && setConfirmLock(null)}>
        <DialogContent className="bg-[#0D0E15] border-[#EF4444]/40 rounded-none max-w-md" data-testid="lockdown-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#EF4444]" />
              {confirmLock === 'lock' ? 'LOCKDOWN CONFIRMATION' : 'RELEASE LOCKDOWN'}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#94A3B8]">
            {confirmLock === 'lock'
              ? 'This will deny SEND_MESSAGES for @everyone across all text channels. Continue?'
              : 'This will restore SEND_MESSAGES for @everyone on all text channels. Continue?'}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmLock(null)} className="rounded-none">CANCEL</Button>
            <Button
              data-testid="lockdown-confirm-btn"
              onClick={() => doLockdown(confirmLock)}
              className={`rounded-none font-bold uppercase ${confirmLock==='lock' ? 'bg-[#EF4444] hover:bg-[#DC2626]' : 'bg-[#10B981] text-black hover:bg-[#10B981]/80'}`}
            >
              {confirmLock === 'lock' ? 'ENGAGE LOCKDOWN' : 'RELEASE'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raid mode configurator */}
      <Dialog open={!!confirmRaid} onOpenChange={(o) => !o && setConfirmRaid(null)}>
        <DialogContent className="bg-[#0D0E15] border-[#F59E0B]/40 rounded-none max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="raid-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
              <Flame className="w-5 h-5 text-[#F59E0B]" />
              {confirmRaid === 'on' ? 'CONFIGURE RAID MODE' : 'STAND DOWN RAID MODE'}
            </DialogTitle>
          </DialogHeader>

          {confirmRaid === 'on' ? (
            <div className="space-y-5">
              {/* Slowmode preset */}
              <div>
                <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2">Slowmode Preset</div>
                <div className="grid grid-cols-5 gap-2">
                  {[5, 10, 30, 60, 120].map(s => (
                    <button
                      key={s}
                      data-testid={`raid-slow-${s}`}
                      onClick={() => setRaidSlowmode(s)}
                      className={`py-3 border font-mono text-xs tracking-widest transition-all ${
                        raidSlowmode === s
                          ? "bg-[#F59E0B] text-black border-[#F59E0B] font-bold"
                          : "border-white/10 text-[#94A3B8] hover:border-[#F59E0B]/60 hover:text-[#F59E0B]"
                      }`}
                    >{s}s</button>
                  ))}
                </div>
                <input
                  data-testid="raid-slow-custom"
                  type="number" min="0" max="21600"
                  value={raidSlowmode}
                  onChange={e => setRaidSlowmode(Math.max(0, Math.min(21600, parseInt(e.target.value || "0"))))}
                  className="mt-2 w-full bg-[#06070A] border border-white/10 px-3 py-2 font-mono text-xs outline-none focus:border-[#F59E0B]"
                  placeholder="Custom (0-21600 seconds)"
                />
              </div>

              {/* What will change */}
              <div>
                <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2">Server Changes</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <SettingChange from={stats?.verification_level || "?"} to="VERY_HIGH" label="Verification" />
                  <SettingChange from={stats?.explicit_content_filter || "?"} to="ALL_MEMBERS" label="Explicit Filter" />
                  <SettingChange from={`${stats?.slowmode_channels ?? 0} channels`} to={`${(stats?.text_channels ?? 0) + (stats?.announcement_channels ?? 0) - raidExempt.size} channels @ ${raidSlowmode}s`} label="Slowmode" />
                  <SettingChange from="—" to={`${raidExempt.size} skipped`} label="Exempted" accent="volt" />
                </div>
              </div>

              {/* Channel exemption picker */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Exempt Channels · {raidExempt.size}/{textChannels.length}</div>
                  <div className="flex gap-2">
                    <button
                      data-testid="raid-exempt-clear"
                      onClick={() => setRaidExempt(new Set())}
                      className="font-mono text-[10px] tracking-widest text-[#94A3B8] hover:text-white"
                    >CLEAR</button>
                    <button
                      data-testid="raid-exempt-mods"
                      onClick={() => {
                        // Auto-exempt channels with "mod", "staff", "log" in name
                        const modChans = textChannels.filter(c => /mod|staff|log|admin/i.test(c.name)).map(c => c.id);
                        setRaidExempt(new Set(modChans));
                      }}
                      className="font-mono text-[10px] tracking-widest text-[#10B981] hover:underline"
                    >AUTO: MOD/STAFF/LOGS</button>
                  </div>
                </div>
                <div className="border border-white/10 max-h-[240px] overflow-y-auto" data-testid="raid-exempt-list">
                  {textChannels.length === 0 && <div className="p-4 text-center font-mono text-xs text-[#64748B]">// no text channels</div>}
                  {textChannels.map(c => {
                    const isExempt = raidExempt.has(c.id);
                    return (
                      <label
                        key={c.id}
                        data-testid={`raid-exempt-${c.id}`}
                        className={`flex items-center gap-2 px-3 py-2 border-b border-white/5 cursor-pointer hover:bg-[#141622] transition-colors ${isExempt ? "bg-[#10B981]/5" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={isExempt}
                          onChange={() => toggleExempt(c.id)}
                          className="accent-[#10B981]"
                        />
                        <Hash className="w-3 h-3 text-[#64748B]" />
                        <span className={`text-sm truncate ${isExempt ? "text-[#10B981]" : ""}`}>{c.name}</span>
                        {c.rate_limit_per_user > 0 && (
                          <span className="ml-auto font-mono text-[10px] text-[#F59E0B]">
                            {c.rate_limit_per_user}s now
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-3 text-xs text-[#F59E0B] font-mono tracking-wider">
                Use this when your guild is under attack. Disable when the raid is over. Bot must have MANAGE_GUILD and MANAGE_CHANNELS perms.
              </div>
            </div>
          ) : (
            <div className="text-sm text-[#94A3B8]">
              This will restore verification to MEDIUM, explicit filter to MEMBERS_WITHOUT_ROLE, and remove slowmode from all text channels. Continue?
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmRaid(null)} className="rounded-none">CANCEL</Button>
            <Button
              data-testid="raid-confirm-btn"
              onClick={() => doRaidMode(confirmRaid)}
              className={`rounded-none font-bold uppercase ${confirmRaid==='on' ? 'bg-[#F59E0B] text-black hover:bg-[#F59E0B]/80' : 'bg-[#10B981] text-black hover:bg-[#10B981]/80'}`}
            >
              {confirmRaid === 'on' ? `ENGAGE @ ${raidSlowmode}s` : 'STAND DOWN'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* First-time guided tour */}
      <Tour
        key={`tour-${tourNonce}`}
        steps={TOUR_STEPS}
        storageKey={tourKey}
        open={tourOpen}
        onClose={() => setTourOpen(false)}
      />
    </div>
  );
}

function SettingChange({ label, from, to, accent = "warn" }) {
  const toColor = accent === "volt" ? "text-[#10B981]" : "text-[#F59E0B]";
  return (
    <div className="border border-white/5 bg-[#06070A] p-3">
      <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">{label}</div>
      <div className="flex items-center gap-2 text-xs font-mono">
        <span className="text-[#64748B] line-through truncate">{from}</span>
        <ChevronRight className="w-3 h-3 text-[#94A3B8] shrink-0" />
        <span className={`${toColor} truncate`}>{to}</span>
      </div>
    </div>
  );
}

function SlashSyncCard({ guildId }) {
  const [commands, setCommands] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const interactionsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/discord/interactions`;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/guilds/${guildId}/slash-commands`);
      setCommands(r.data.commands || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [guildId]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post(`/guilds/${guildId}/slash-sync`);
      toast.success(`Registered ${r.data.count} slash commands`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const copy = async (v, label) => {
    try {
      await navigator.clipboard.writeText(v);
      toast.success(`${label} copied`);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="border border-white/10 bg-[#0D0E15] p-5" data-testid="slash-sync-card">
      <div className="flex items-center gap-2 mb-4">
        <Terminal className="w-4 h-4 text-[#10B981]" />
        <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Slash Command Sync</span>
        <span className="ml-auto flex items-center gap-1 px-2 py-1 font-mono text-[10px] tracking-widest border border-white/10 text-[#94A3B8]">
          {commands.length} REGISTERED
        </span>
      </div>
      <p className="text-xs text-[#94A3B8] mb-4">
        Register mod commands as Discord slash commands so mods can run them from inside Discord (e.g. <code className="text-[#10B981]">/ban</code>, <code className="text-[#10B981]">/warn</code>). Same role-based access rules apply.
      </p>

      <div className="mb-4">
        <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Interactions Endpoint URL (paste in Discord Portal → General Information)</label>
        <div className="flex items-center gap-2 bg-[#06070A] border border-white/10 p-2.5">
          <code data-testid="interactions-url" className="flex-1 font-mono text-xs text-[#10B981] truncate">{interactionsUrl}</code>
          <button
            data-testid="copy-interactions-url"
            onClick={() => copy(interactionsUrl, "Interactions URL")}
            className="px-2.5 py-1.5 bg-[#10B981] text-black font-mono text-[10px] tracking-widest font-bold hover:bg-[#10B981]/80"
          >COPY</button>
        </div>
        <p className="mt-2 font-mono text-[10px] text-[#64748B] tracking-wider">
          Also set <span className="text-[#8B5CF6]">DISCORD_PUBLIC_KEY</span> in backend/.env with your app's Public Key (Discord Portal → General Information → Public Key), then restart backend.
        </p>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <Button
          data-testid="slash-sync-btn"
          onClick={sync}
          disabled={syncing}
          className="rounded-none bg-[#10B981] text-black hover:bg-[#10B981]/80 font-bold"
        >
          <RotateCcw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "SYNCING..." : "SYNC COMMANDS"}
        </Button>
        <span className="font-mono text-[10px] text-[#64748B]">Replaces all guild-scoped slash commands for ModNode</span>
      </div>

      <div className="border border-white/10 max-h-[280px] overflow-y-auto" data-testid="slash-list">
        {loading && <div className="p-4 font-mono text-xs text-[#64748B]">// loading...</div>}
        {!loading && commands.length === 0 && (
          <div className="p-4 font-mono text-xs text-[#64748B]">// no slash commands registered yet — hit SYNC COMMANDS</div>
        )}
        {commands.map(c => (
          <div key={c.id} className="px-4 py-2 border-b border-white/5 flex items-center gap-3 text-xs">
            <span className="font-mono text-[#10B981]">/{c.name}</span>
            <span className="text-[#94A3B8] truncate flex-1">{c.description}</span>
            {c.options?.length > 0 && (
              <span className="font-mono text-[10px] text-[#64748B]">{c.options.length} args</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CmdCard({ cmd, roles, channels, form, setForm, savePerm, onRun }) {  const [expanded, setExpanded] = useState(false);
  const accent = cmd.danger === "danger" ? "border-[#EF4444]/40" : cmd.danger === "warn" ? "border-[#F59E0B]/40" : cmd.danger === "volt" ? "border-[#10B981]/40" : "border-[#8B5CF6]/40";
  const btnColor = cmd.danger === "danger" ? "bg-[#EF4444] hover:bg-[#DC2626] text-white" : cmd.danger === "warn" ? "bg-[#F59E0B] hover:bg-[#e6a600] text-black" : cmd.danger === "volt" ? "bg-[#10B981] hover:bg-[#10B981]/80 text-black" : "bg-[#8B5CF6] hover:bg-[#8B5CF6]/80 text-black";
  const needsUser = ["warn", "timeout", "untimeout", "kick", "ban", "unban", "nickname", "role_add", "role_remove"].includes(cmd.key);
  const needsChannel = ["purge", "slowmode"].includes(cmd.key);
  const allowed = new Set(cmd.allowed_role_ids || []);
  const toggleRole = (rid) => {
    const next = new Set(allowed);
    if (next.has(rid)) next.delete(rid); else next.add(rid);
    savePerm(cmd.key, Array.from(next));
  };

  return (
    <div className={`border ${accent} bg-[#0D0E15] p-4`} data-testid={`cmd-${cmd.key}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-display font-black text-lg uppercase tracking-tight">{cmd.label}</div>
          <div className="text-xs text-[#94A3B8]">{cmd.desc}</div>
        </div>
        <button
          onClick={() => setExpanded(v => !v)}
          data-testid={`cmd-toggle-${cmd.key}`}
          className="font-mono text-[10px] tracking-widest text-[#64748B] hover:text-white"
        >{expanded ? "COLLAPSE" : "EXPAND"}</button>
      </div>

      {/* Quick run form */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        {needsUser && (
          <input
            data-testid={`cmd-${cmd.key}-userid`}
            value={form.user_id || ""}
            onChange={e => setForm(cmd.key, "user_id", e.target.value)}
            placeholder="user_id"
            className="col-span-2 bg-[#06070A] border border-white/10 px-3 py-2 text-xs outline-none focus:border-[#10B981]"
          />
        )}
        {needsChannel && (
          <select
            data-testid={`cmd-${cmd.key}-channel`}
            value={form.channel_id || ""}
            onChange={e => setForm(cmd.key, "channel_id", e.target.value)}
            className="col-span-2 bg-[#06070A] border border-white/10 px-3 py-2 text-xs outline-none focus:border-[#10B981]"
          >
            <option value="">Pick channel...</option>
            {channels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
        )}
        {cmd.key === "timeout" && (
          <input
            data-testid={`cmd-timeout-minutes`}
            value={form.minutes || ""}
            onChange={e => setForm(cmd.key, "minutes", e.target.value)}
            placeholder="minutes"
            className="bg-[#06070A] border border-white/10 px-3 py-2 text-xs outline-none focus:border-[#10B981]"
          />
        )}
        {cmd.key === "ban" && (
          <input
            value={form.purge_seconds || ""}
            onChange={e => setForm(cmd.key, "purge_seconds", e.target.value)}
            placeholder="msg purge sec (0-604800)"
            className="bg-[#06070A] border border-white/10 px-3 py-2 text-xs outline-none focus:border-[#10B981]"
          />
        )}
        {cmd.key === "purge" && (
          <input
            value={form.count || ""}
            onChange={e => setForm(cmd.key, "count", e.target.value)}
            placeholder="count (1-100)"
            className="bg-[#06070A] border border-white/10 px-3 py-2 text-xs outline-none focus:border-[#10B981]"
          />
        )}
        {cmd.key === "slowmode" && (
          <input
            value={form.seconds || ""}
            onChange={e => setForm(cmd.key, "seconds", e.target.value)}
            placeholder="seconds (0-21600)"
            className="bg-[#06070A] border border-white/10 px-3 py-2 text-xs outline-none focus:border-[#10B981]"
          />
        )}
        {cmd.key === "raidmode" && (
          <input
            value={form.slowmode || ""}
            onChange={e => setForm(cmd.key, "slowmode", e.target.value)}
            placeholder="slowmode (10)"
            className="bg-[#06070A] border border-white/10 px-3 py-2 text-xs outline-none focus:border-[#10B981]"
          />
        )}
        {cmd.key === "nickname" && (
          <input
            value={form.nick || ""}
            onChange={e => setForm(cmd.key, "nick", e.target.value)}
            placeholder="new nickname"
            className="bg-[#06070A] border border-white/10 px-3 py-2 text-xs outline-none focus:border-[#10B981]"
          />
        )}
        {(cmd.key === "role_add" || cmd.key === "role_remove") && (
          <select
            value={form.role_id || ""}
            onChange={e => setForm(cmd.key, "role_id", e.target.value)}
            className="bg-[#06070A] border border-white/10 px-3 py-2 text-xs outline-none focus:border-[#10B981]"
          >
            <option value="">Pick role...</option>
            {roles.filter(r => r.name !== "@everyone").map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        )}
        {!["untimeout", "unban", "lockdown", "unlock"].includes(cmd.key) && (
          <input
            value={form.reason || ""}
            onChange={e => setForm(cmd.key, "reason", e.target.value)}
            placeholder="reason (optional)"
            className="col-span-2 bg-[#06070A] border border-white/10 px-3 py-2 text-xs outline-none focus:border-[#10B981]"
          />
        )}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <Button
          data-testid={`cmd-run-${cmd.key}`}
          onClick={onRun}
          className={`rounded-none font-bold uppercase font-mono text-xs tracking-widest ${btnColor}`}
        >
          <Play className="w-3 h-3 mr-1.5" /> RUN
        </Button>
        <span className="font-mono text-[10px] text-[#64748B]">
          {allowed.size === 0 ? "admin-only" : `${allowed.size} role(s) allowed`}
        </span>
      </div>

      {expanded && (
        <div className="pt-3 border-t border-white/5">
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2">Allowed Roles</div>
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-testid={`cmd-roles-${cmd.key}`}>
            {roles.filter(r => r.name !== "@everyone").map(r => {
              const on = allowed.has(r.id);
              return (
                <button
                  key={r.id}
                  onClick={() => toggleRole(r.id)}
                  className={`px-2 py-1 border font-mono text-[10px] tracking-widest transition-all flex items-center gap-1 ${on ? "bg-[#10B981]/10 border-[#10B981] text-[#10B981]" : "border-white/10 text-[#94A3B8] hover:border-white/30"}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: r.color ? `#${r.color.toString(16).padStart(6,'0')}` : "#64748B" }} />
                  {r.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ActionBtn({ icon: Icon, label, color, onClick, testid }) {
  const colors = {
    cyan: "border-[#8B5CF6]/40 hover:border-[#8B5CF6] hover:text-[#8B5CF6]",
    volt: "border-[#10B981]/40 hover:border-[#10B981] hover:text-[#10B981]",
    warn: "border-[#F59E0B]/40 hover:border-[#F59E0B] hover:text-[#F59E0B]",
    danger: "border-[#EF4444]/40 hover:border-[#EF4444] hover:text-[#EF4444]",
  };
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-2 py-1.5 border ${colors[color]} font-mono text-[10px] tracking-widest transition-all flex items-center gap-1`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
}
